
import { lazy, Suspense } from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "react-hot-toast";

// WebSocket & Security
import { WebSocketProvider } from "./contexts/WebSocketContext";

// Auth / guards
import ProtectedRoute from "./components/ProtectedRoute";
import Colors from "./theme/colors";

// Modules / dashboards
const Login = lazy(() => import("./pages/login/Login"));
const Dashboard = lazy(() => import("./pages/module/Dashboard"));
const ModulePage = lazy(() => import("./pages/module/ModulePage"));
const ReservationPage = lazy(() => import("./pages/module/ReservationPage"));
const BackofficePage = lazy(() => import("./pages/module/BackofficePage"));
const PosPage = lazy(() => import("./pages/module/PosPage"));
const FrontdeskPage = lazy(() => import("./pages/module/FrontdeskPage"));
const ReportPage = lazy(() => import("./pages/module/ReportPage"));
const InventoryPage = lazy(() => import("./pages/module/InventoryPage"));
const MaterialPage = lazy(() => import("./pages/module/MaterialPage"));
const KdsPage = lazy(() => import("./pages/module/KdsPage"));
const HousekeepingPage = lazy(() => import("./pages/module/HousekeepingPage"));
const BookingEnginePage = lazy(() => import("./pages/module/BookingEnginePage"));
const GuestDetailsPage = lazy(() => import("./pages/BookingEngine/GuestDetailsPage"));
const BookingSummaryPage = lazy(() => import("./pages/BookingEngine/BookingSummaryPage"));
const PaymentPage = lazy(() => import("./pages/BookingEngine/PaymentPage"));
const ConfirmationPage = lazy(() => import("./pages/BookingEngine/ConfirmationPage"));
const AccountingPage = lazy(() => import("./pages/module/AccountingPage"));
const BanquetPage = lazy(() => import("./pages/module/BanquetPage"));
const PropertyProfile = lazy(() => import("./pages/Backoffice/property/PropertyProfile"));
const SalesPerson = lazy(() => import("./pages/Backoffice/property/SalesPerson"));
const DesignationPage = lazy(() => import("./pages/Backoffice/masters/Designation"));
const KDSsettings = lazy(() => import("./pages/Backoffice/masters/KDSsetting"));
const Ledger = lazy(() => import("./pages/Backoffice/masters/ledger"));
const VisitPurpose = lazy(() => import("./pages/Backoffice/masters/VisitPurpose"));
const PickDropFacility = lazy(() => import("./pages/Backoffice/masters/PickDropFacility"));
const BillingInstruction = lazy(() => import("./pages/Backoffice/masters/BillingInstruction"));
const IdentityDetails = lazy(() => import("./pages/Backoffice/masters/IdentityDetails"));
const VersionPage = lazy(() => import("./pages/Backoffice/masters/Version"));
const StateMaster = lazy(() => import("./pages/Backoffice/masters/State"));
const CityMaster = lazy(() => import("./pages/Backoffice/masters/City"));
const AreaMaster = lazy(() => import("./pages/Backoffice/masters/Area"));
const ZoneMaster = lazy(() => import("./pages/Backoffice/masters/Zone"));
const TaxSlabMaster = lazy(() => import("./pages/Backoffice/masters/TaxSlab"));
const TaxProcessMaster = lazy(() => import("./pages/Backoffice/masters/TaxProcess"));
const TaxRateNameMaster = lazy(() => import("./pages/Backoffice/masters/Taxratename"));
const OrderCancelTimeMaster = lazy(() => import("./pages/Backoffice/masters/OrderCancelTime"));
const BillNoSettingMaster = lazy(() => import("./pages/Backoffice/masters/BillNoSetting"));
const ReservationSettings = lazy(() => import("./pages/Backoffice/masters/ReservationSettings"));
const PinCode = lazy(() => import("./pages/Backoffice/masters/Pincode"));
const PropertyInformation = lazy(() => import("./pages/Backoffice/masters/PropertyInformation"));
const PropertyInformationApp = lazy(() => import("./pages/Backoffice/masters/PropertyInformationApp"));
const ImageDeliveryApp = lazy(() => import("./pages/Backoffice/masters/ImageDeliveryApp"));
const PickUpTime = lazy(() => import("./pages/Backoffice/masters/PickUpTime"));
const EmployeeType = lazy(() => import("./pages/Backoffice/masters/EmployeeType"));
const Employee = lazy(() => import("./pages/Backoffice/masters/Employee"));
const BankLedger = lazy(() => import("./pages/Backoffice/masters/BankLedger"));
const MemberCard = lazy(() => import("./pages/Backoffice/masters/MemberCard"));
const TopupBonus = lazy(() => import("./pages/Backoffice/masters/TopupBonus"));
const GstMaster = lazy(() => import("./pages/Backoffice/masters/GstMaster"));
const LanguageMaster = lazy(() => import("./pages/Backoffice/masters/LanguageMaster"));
const RolePermissions = lazy(() => import("./pages/Backoffice/masters/RolePermissions"));
const AuditLogViewer = lazy(() => import("./pages/Backoffice/masters/AuditLogViewer"));
const ChainBrandMaster = lazy(() => import("./pages/Backoffice/org/ChainBrandMaster"));
const LegalEntityMaster = lazy(() => import("./pages/Backoffice/org/LegalEntityMaster"));
const NightAuditSetting = lazy(() => import("./pages/Backoffice/setting/Nightaudit"));
const CommonSetting = lazy(() => import("./pages/Backoffice/setting/Commonsetting"));
const Bookingnositting = lazy(() => import("./pages/Backoffice/setting/BookingNoSetting"));
const CopyTableColumnCRM = lazy(() => import("./pages/Backoffice/setting/CopyTableColumnCRM"));
const UnitPage = lazy(() => import("./pages/Backoffice/commonmaster/unit"));
const SubUnitPage = lazy(() => import("./pages/Backoffice/commonmaster/Subunit"));
const FnbBillingTypePage = lazy(() => import("./pages/Backoffice/commonmaster/FnbBillingType"));
const CurrencyPage = lazy(() => import("./pages/Backoffice/commonmaster/Currency"));
const GuestTypePage = lazy(() => import("./pages/Backoffice/commonmaster/GuestType"));
const BrandPage = lazy(() => import("./pages/Backoffice/commonmaster/Brand"));
const DealsPage = lazy(() => import("./pages/Backoffice/commonmaster/Deals"));
const VoidReasonsPage = lazy(() => import("./pages/Backoffice/commonmaster/VoidReasons"));
const KotBookManualPage = lazy(() => import("./pages/Backoffice/commonmaster/KotBookManual"));
const PrinterSetPage = lazy(() => import("./pages/Backoffice/commonmaster/PrinterSet"));
const VegTypePage = lazy(() => import("./pages/Backoffice/commonmaster/VegType"));
const EmailerCreationPage = lazy(() => import("./pages/Backoffice/commonmaster/EmailerCreation"));
const EmailSettingPage = lazy(() => import("./pages/Backoffice/commonmaster/EmailSetting"));
const EmailFooterPage = lazy(() => import("./pages/Backoffice/commonmaster/EmailFooter"));
const RegDocHeaderChangePage = lazy(() => import("./pages/Backoffice/commonmaster/DocumentHeaderText"));
const PrinterSettingPage = lazy(() => import("./pages/Backoffice/commonmaster/PrinterSetting"));
const InvoiceConfiguration = lazy(() => import("./pages/Backoffice/Config/InvoiceConfiguration"));
const LedgerMappingPage = lazy(() => import("./pages/Backoffice/commonmaster/LedgerMapping"));
const TaxLedgerMappingPage = lazy(() => import("./pages/Backoffice/commonmaster/TaxLedgerMapping"));
const MappingCheckPage = lazy(() => import("./pages/Backoffice/commonmaster/MappingCheck"));
const WhatsAppSettingPage = lazy(() => import("./pages/Backoffice/commonmaster/WhatsAppSetting"));
const DeleteNightAuditDatePage = lazy(() => import("./pages/Backoffice/commonmaster/DeleteNightAuditDate"));
const BusinessSourcePage = lazy(() => import("./pages/Backoffice/commonmaster/BusinessSource"));
const SmsTemplatePage = lazy(() => import("./pages/Backoffice/commonmaster/WhatsappSmsTemplate"));
const AddClientWhatsAppPage = lazy(() => import("./pages/Backoffice/commonmaster/WhatsAppClient"));
const SubmitTemplatePage = lazy(() => import("./pages/Backoffice/commonmaster/WhatsAppTemplate"));
const AddWhatsAppTemplatePage = lazy(() => import("./pages/Backoffice/commonmaster/whatsappTemplateuser"));
const QRMenuSettingPage = lazy(() => import("./pages/Backoffice/Qrmaster/QRMenuSetting"));
const AddPosCustomerSetting = lazy(() => import("./pages/Backoffice/fnb/AddPosCustomerSetting"));
const CouponMaster = lazy(() => import("./pages/Backoffice/fnb/CouponMaster"));
const Outlet = lazy(() => import("./pages/Backoffice/fnb/Outlet"));
const TablePage = lazy(() => import("./pages/Backoffice/fnb/Table"));
const ParentCategoryPage = lazy(() => import("./pages/Backoffice/fnb/ParentCategory"));
const CategoryPage = lazy(() => import("./pages/Backoffice/fnb/Category"));
const MenuGroupPage = lazy(() => import("./pages/Backoffice/fnb/MenuGroup"));
const ItemPage = lazy(() => import("./pages/Backoffice/fnb/Item"));
const AddItemToOutletPage = lazy(() => import("./pages/Backoffice/fnb/AddItemToOutlet"));
const OutOfStock = lazy(() => import("./pages/Backoffice/fnb/OutOfStock"));
const AddKitchen = lazy(() => import("./pages/Backoffice/fnb/AddKitchen"));
const KitchenTransferOutlet = lazy(() => import("./pages/Backoffice/fnb/KitchenTransferOutlet"));
const BanquetGroup = lazy(() => import("./pages/Backoffice/fnb/BanquetGroup"));
const LoyaltySetting = lazy(() => import("./pages/Backoffice/fnb/LoyaltySetting"));
const RecipePage = lazy(() => import("./pages/Backoffice/fnb/Recipe"));
const ReservationCancelSetting = lazy(() => import("./pages/Backoffice/Room/ReservationCancelSetting"));
const BlockMaster = lazy(() => import("./pages/Backoffice/Room/BlockMaster"));
const FloorMaster = lazy(() => import("./pages/Backoffice/Room/FloorMaster"));
const RoomViewType = lazy(() => import("./pages/Backoffice/Room/RoomViewType"));
const BedSize = lazy(() => import("./pages/Backoffice/Room/BedSize"));
const AmenityCategoryMaster = lazy(() => import("./pages/Backoffice/Room/AmenityCategory"));
const AmenitySubCategoryMaster = lazy(() => import("./pages/Backoffice/Room/AmenitySubcategory"));
const RoomCategory = lazy(() => import("./pages/Backoffice/Room/RoomCategory"));
const RoomPlanType = lazy(() => import("./pages/Backoffice/Room/RoomPlanType"));
const RoomType = lazy(() => import("./pages/Backoffice/Room/RoomType"));
const RoomNoMaster = lazy(() => import("./pages/Backoffice/Room/RoomNoMaster"));
const RackRatePlanMaster = lazy(() => import("./pages/Backoffice/Room/RackRatePlanMaster"));
const RoomServiceTypeMaster = lazy(() => import("./pages/Backoffice/Room/RoomServiceTypeMaster"));
const RoomRatePlan = lazy(() => import("./pages/Backoffice/Room/RoomRatePlan"));
const RoomRackRatePlan = lazy(() => import("./pages/Backoffice/Room/RoomRackRatePlan"));
const RoomOffer = lazy(() => import("./pages/Backoffice/Room/RoomOffer"));
const ChannelManagerSetting = lazy(() => import("./pages/Backoffice/Room/ChannelManagerSettingOta"));
const CategoryDesignationMap = lazy(() => import("./pages/Backoffice/Room/CategoryDesignationMap"));
const ChannelManagerCodeSet = lazy(() => import("./pages/Backoffice/Room/ChannelManagerCodeSetting"));
const MealPlanConfig = lazy(() => import("./pages/Backoffice/Room/MealPlanConfig"));
const CurrencySetup = lazy(() => import("./pages/Backoffice/currency/Setup"));
const CurrencyExchange = lazy(() => import("./pages/Backoffice/currency/Exchange"));
const NcCosting = lazy(() => import("./pages/Backoffice/nc/NcCosting"));
const Department = lazy(() => import("./pages/Backoffice/nc/Department"));
const UserList = lazy(() => import("./pages/Backoffice/report/UserList"));
const FeedbackList = lazy(() => import("./pages/Backoffice/report/FeedbackList"));
const ComplainType = lazy(() => import("./pages/Backoffice/complain/ComplainType"));
const ComplainUserList = lazy(() => import("./pages/Backoffice/complain/ComplainUserList"));
const ProductionFinish = lazy(() => import("./pages/Backoffice/production/ProductionFinish"));
const AddProduction = lazy(() => import("./pages/Backoffice/production/AddProduction"));
const ProductionProcess = lazy(() => import("./pages/Backoffice/production/ProductionProcess"));
const UserRole = lazy(() => import("./pages/Backoffice/UserManagement/UserRole"));
const UserCreation = lazy(() => import("./pages/Backoffice/UserManagement/UserCreation"));
const UserRights = lazy(() => import("./pages/Backoffice/UserManagement/UserRights"));
const UserAuthorization = lazy(() => import("./pages/Backoffice/UserManagement/UserAuthorization"));
const LedgerExportPage = lazy(() => import("./pages/Backoffice/accounts/LedgerExport"));
const VoucherExportPage = lazy(() => import("./pages/Backoffice/accounts/VoucherExport"));
const MemberShipType = lazy(() => import("./pages/Backoffice/membership/MemberShipType"));
const MemberShipService = lazy(() => import("./pages/Backoffice/membership/MemberShipService"));
const MemberShipPlan = lazy(() => import("./pages/Backoffice/membership/MemberShipPlan"));
const MemberShipPlanDetails = lazy(() => import("./pages/Backoffice/membership/MemberShipPlanDetails"));
const NewReservation = lazy(() => import("./pages/frontdesk/NewReservation"));
const ConfirmationVoucher = lazy(() => import("./pages/frontdesk/ConfirmationVoucher"));
const ReservationDetails = lazy(() => import("./pages/frontdesk/Reservationdetails"));
const ReservationStatusView = lazy(() => import("./pages/frontdesk/ReservationStatusView"));
const CancelReservationList = lazy(() => import("./pages/frontdesk/CancelReservationList"));
const ReservationCalendar = lazy(() => import("./pages/frontdesk/ReservationCalender"));
const AdvanceDeposit = lazy(() => import("./pages/frontdesk/AdvanceDeposit"));
const ReturnPaidup = lazy(() => import("./pages/frontdesk/ReturnPaidup"));
const NoShowRoomReport = lazy(() => import("./pages/frontdesk/NoShowRoomReport"));
const CrossPropertyAvailability = lazy(() => import("./pages/frontdesk/CrossPropertyAvailability"));
const GuestRequests = lazy(() => import("./pages/frontdesk/GuestRequests"));
const GuestMaster = lazy(() => import("./pages/guests/GuestMaster"));

// Engineering / Operations
const PMSchedules = lazy(() => import("./pages/Engineering/PMSchedules"));
const ShiftLogbook = lazy(() => import("./pages/Operations/ShiftLogbook"));
const BookingSheetAccounts = lazy(() => import("./pages/frontdesk/BookingSheetAccounts"));
const ReservationAdvancePendingFO = lazy(() => import("./pages/frontdesk/ReservationpendingReports"));
const PreRegCard = lazy(() => import("./pages/frontdesk/PreRegCard"));
const CheckInGuest = lazy(() => import("./pages/frontdesk/CheckInGuest"));
const DirectCheckInGuest = lazy(() => import("./pages/frontdesk/DirectCheckInGuest"));
const CheckInGuestDetails = lazy(() => import("./pages/frontdesk/CheckInGuestDetails"));
const PaxCheckin = lazy(() => import("./pages/frontdesk/PaxCheckin"));
const CancelBookingDetails = lazy(() => import("./pages/frontdesk/CancelBookingDetails"));
const RoomCalendar = lazy(() => import("./pages/frontdesk/RoomCalendar"));
const LinkedUnlinkedReport = lazy(() => import("./pages/frontdesk/LinkedUnlinkedReport"));
const CheckOutGuest = lazy(() => import("./pages/frontdesk/CheckOutGuest"));
const Calendar = lazy(() => import("./pages/frontdesk/Calendar"));
const GuestCheckoutExtend = lazy(() => import("./pages/frontdesk/GuestCheckoutExtend"));
const CheckoutGuestdetails = lazy(() => import("./pages/frontdesk/CheckoutGuestdetails"));
const PostCharges = lazy(() => import("./pages/frontdesk/PostCharges"));
const PostingMismatch = lazy(() => import("./pages/frontdesk/PostingMismatch"));
const BookingLink = lazy(() => import("./pages/frontdesk/BookingLink"));
const Settlement = lazy(() => import("./pages/frontdesk/Settlement"));
const NightAuditProcess = lazy(() => import("./pages/frontdesk/NightAuditProcess"));
const PrintBill = lazy(() => import("./pages/frontdesk/PrintBill"));
const RoomShifting = lazy(() => import("./pages/frontdesk/RoomShifting"));
const PaymentExpense = lazy(() => import("./pages/frontdesk/PettyCash/PaymentExpense"));
const Receipt = lazy(() => import("./pages/frontdesk/PettyCash/Receipt"));
const ExpenseSummaryReport = lazy(() => import("./pages/frontdesk/PettyCash/ExpenseSummaryReport"));
const ExpenseHeadMaster = lazy(() => import("./pages/frontdesk/PettyCash/ExpenseHeadMaster"));
const ReceiveHeadMaster = lazy(() => import("./pages/frontdesk/PettyCash/ReceiveHeadMaster"));
const OtherHotelReservation = lazy(() => import("./pages/frontdesk/OtherHotel/OtherHotelReservation"));
const StarsRequest = lazy(() => import("./pages/frontdesk/OtherHotel/StarsRequest"));
const OtherHotelBookingDetails = lazy(() => import("./pages/frontdesk/OtherHotel/OtherHotelBookingDetails"));
const MemberRegistration = lazy(() => import("./pages/frontdesk/Club/MemberRegistration"));
const MembershipDetails = lazy(() => import("./pages/frontdesk/Club/MembershipDetails"));
const MembershipAllotment = lazy(() => import("./pages/frontdesk/Club/MembershipAllotment"));
const AllotmentDetails = lazy(() => import("./pages/frontdesk/Club/AllotmentDetails"));
const MembershipEnrollment = lazy(() => import("./pages/frontdesk/Club/MembershipEnrollment"));
const RoomStatusBoard = lazy(() => import("./pages/Housekeeping/RoomStatusBoard"));
const AssignTasks = lazy(() => import("./pages/Housekeeping/AssignTasks"));
const CleaningSchedule = lazy(() => import("./pages/Housekeeping/CleaningSchedule"));
const RoomInspection = lazy(() => import("./pages/Housekeeping/RoomInspection"));
const TurnDownService = lazy(() => import("./pages/Housekeeping/TurnDownService"));
const DeepCleaningPlanner = lazy(() => import("./pages/Housekeeping/DeepCleaningPlanner"));
const LostAndFound = lazy(() => import("./pages/Housekeeping/LostAndFound"));
const MaintenanceRequests = lazy(() => import("./pages/Housekeeping/MaintenanceRequests"));
const LinenInventory = lazy(() => import("./pages/Housekeeping/LinenInventory"));
const MinibarRefill = lazy(() => import("./pages/Housekeeping/MinibarRefill"));
const LaundryReceipt = lazy(() => import("./pages/Housekeeping/LaundryReceipt"));
const LaundryBilling = lazy(() => import("./pages/Housekeeping/LaundryBilling"));
const LaundrySettlement = lazy(() => import("./pages/Housekeeping/LaundrySettlement"));
const MinibarBilling = lazy(() => import("./pages/Housekeeping/MinibarBilling"));
const MinibarSettlement = lazy(() => import("./pages/Housekeeping/MinibarSettlement"));
const LaundererIssue = lazy(() => import("./pages/Housekeeping/LaundererIssue"));
const ReturnIssue = lazy(() => import("./pages/Housekeeping/ReturnIssue"));
const Handover = lazy(() => import("./pages/Housekeeping/Handover"));
const HKStaffMaster = lazy(() => import("./pages/Housekeeping/master/StaffMaster"));
const HKDeliveryTypes = lazy(() => import("./pages/Housekeeping/master/DeliveryTypes"));
const HKServiceTypes = lazy(() => import("./pages/Housekeeping/master/ServiceTypes"));
const HKLocation = lazy(() => import("./pages/Housekeeping/master/Location"));
const HKLaundryItemMaster = lazy(() => import("./pages/Housekeeping/master/LaundryItemMaster"));
const HKMinibarItemMaster = lazy(() => import("./pages/Housekeeping/master/MinibarItemMaster"));
const HKLaundererMaster = lazy(() => import("./pages/Housekeeping/master/LaundererMaster"));
const Productivity = lazy(() => import("./pages/Housekeeping/Reports/Productivity"));
const StatusHistory = lazy(() => import("./pages/Housekeeping/Reports/StatusHistory"));
const LostFoundLog = lazy(() => import("./pages/Housekeeping/Reports/LostFoundLog"));
const MaterialsUsage = lazy(() => import("./pages/Housekeeping/Reports/MaterialsUsage"));
const TaskTypes = lazy(() => import("./pages/Housekeeping/settings/TaskTypes"));
const Statuses = lazy(() => import("./pages/Housekeeping/settings/HKStatuses"));
const Checklists = lazy(() => import("./pages/Housekeeping/settings/Checklists"));
const StaffTeams = lazy(() => import("./pages/Housekeeping/settings/StaffAndTeams"));
const Downsend = lazy(() => import("./pages/Pos/Restaurant/DownSend"));
const TablePlan = lazy(() => import("./pages/Pos/Restaurant/TablePlan"));
const Bar = lazy(() => import("./pages/Pos/Bar"));
const RoomService = lazy(() => import("./pages/Pos/RoomService"));
const Laundry = lazy(() => import("./pages/Pos/HOUSE KEEPING/Laundry"));
const Swargtrustify = lazy(() => import("./pages/Banquet/Banquet/SwargTrustify"));
const IssueMaterial = lazy(() => import("./pages/Pos/Inventory/InventoryIssue"));
const IndentDetails = lazy(() => import("./pages/Pos/Inventory/InventoryIndent"));
const StockManualConsumption = lazy(() => import("./pages/Pos/Inventory/InventoryStockConsumption"));
const StockAdjustment = lazy(() => import("./pages/Pos/Inventory/InventoryStockAdjust"));
const StockTransfer = lazy(() => import("./pages/Pos/Inventory/InventorystockTransfer"));
const DepartmentOpStock = lazy(() => import("./pages/Pos/Inventory/DepartmentStock"));
const StockVerification = lazy(() => import("./pages/Pos/Inventory/InventoryVerificationDeatail"));
const InventoryStockDetail = lazy(() => import("./pages/Pos/Inventory/InventoryStockDeatail"));
const ItemInventory = lazy(() => import("./pages/Pos/InventoryReport/ItemIventoryReport"));
const IssueTransaction = lazy(() => import("./pages/Pos/InventoryReport/ItemTransactionReport"));
const NonMovingItems = lazy(() => import("./pages/Pos/InventoryReport/NonMovingItemReport"));
const ManualConsumptionReport = lazy(() => import("./pages/Pos/InventoryReport/StockManualReport"));
const BillReport = lazy(() => import("./pages/Pos/Reports/BillReport"));
const FnbSummary = lazy(() => import("./pages/Pos/Reports/FnbSummaryReport"));
const OrderSummary = lazy(() => import("./pages/Pos/Reports/OrderSummaryReport"));
const TallyReport = lazy(() => import("./pages/Pos/Reports/TallyReport"));
const PrinterSettings = lazy(() => import("./pages/Pos/Settings/PrinterSettings"));
const PrinterExe = lazy(() => import("./pages/Pos/Settings/PrinterExe"));
const Category = lazy(() => import("./pages/Pos/Purchase/Category"));
const SubCategory = lazy(() => import("./pages/Pos/Purchase/SubCategory"));
const ItemMaster = lazy(() => import("./pages/Pos/Purchase/ItemMaster"));
const PurchaseDetails = lazy(() => import("./pages/Pos/Purchase/PurchaseDeatail"));
const PurchaseChallan = lazy(() => import("./pages/Pos/Purchase/PurchaseChallan"));
const PurchaseOrder = lazy(() => import("./pages/Pos/Purchase/PurchaseOrder"));
const TransactionReport = lazy(() => import("./pages/Pos/Purchase/PurchaseTransactionReport"));
const AddSupplier = lazy(() => import("./pages/Pos/Purchase/AddSupplier"));
const PurchaseRequisition = lazy(() => import("./pages/Pos/Purchase/PurchaseRequisition"));
const BookingDetail = lazy(() => import("./pages/Banquet/Banquet/BanquetBookingDeatail"));
const BanquetCalendar = lazy(() => import("./pages/Banquet/Banquet/BanquetCalendar"));
const BanquetTallyReport = lazy(() => import("./pages/Banquet/Banquet/BanquetTallyReport"));
const BanquetBookings = lazy(() => import("./pages/Banquet/Banquet/BanquetBookings"));
const BanquetEnquiries = lazy(() => import("./pages/Banquet/Banquet/BanquetEnquiries"));
const HallAvailability = lazy(() => import("./pages/Banquet/Banquet/HallAvailability"));
const BanquetInvoices = lazy(() => import("./pages/Banquet/Banquet/BanquetInvoices"));
const BanquetSettlements = lazy(() => import("./pages/Banquet/Banquet/BanquetSettlements"));
const BanquetRevenueReports = lazy(() => import("./pages/Banquet/Banquet/BanquetRevenueReports"));
const BanquetHalls = lazy(() => import("./pages/Banquet/Banquet/SettingsHalls"));
const BanquetMenus = lazy(() => import("./pages/Banquet/Banquet/SettingsMenus"));
const BanquetKpiReports = lazy(() => import("./pages/Banquet/Banquet/BanquetReports"));
const Arrival = lazy(() => import("./pages/Reports/Reservation/ArrivalList"));
const ReservationAdvancePending = lazy(() => import("./pages/Reports/Reservation/pendingReports"));
const ComplimentaryStay = lazy(() => import("./pages/Reports/Reservation/ComplimentaryStay"));
const ContributionAnalysis = lazy(() => import("./pages/Reports/Reservation/ContributionAnalysis"));
const CancelReservation = lazy(() => import("./pages/Reports/Reservation/CancelReservation"));
const Depature = lazy(() => import("./pages/Reports/Reservation/DepartureList"));
const DepartureForTheDay = lazy(() => import("./pages/Reports/Reservation/DepartureForTheDay"));
const NoShowReservation = lazy(() => import("./pages/Reports/Reservation/NoShowReservation"));
const ReservationForcast = lazy(() => import("./pages/Reports/Reservation/ReservationForcastReport"));
const RoomForcasting = lazy(() => import("./pages/Reports/Reservation/RoomForcasting"));
const CompanyForecastReport = lazy(() => import("./pages/Reports/Reservation/CompanyForecastReport"));
const SaleRevenue = lazy(() => import("./pages/Reports/Reservation/SaleRevenue"));
const AdvancePayment = lazy(() => import("./pages/Reports/FrontOffice/AdvancePayment"));
const BillSummary = lazy(() => import("./pages/Reports/FrontOffice/BillSummary"));
const BusinessSourceReport = lazy(() => import("./pages/Reports/FrontOffice/BusinessSourceReport"));
const CompanyAnalysis = lazy(() => import("./pages/Reports/FrontOffice/CompanyAnalysis"));
const DaywiseTariffDiscount = lazy(() => import("./pages/Reports/FrontOffice/DaywiseTariffDiscount"));
const DetailPosition = lazy(() => import("./pages/Reports/FrontOffice/DetailPosition"));
const CustomerDetails = lazy(() => import("./pages/Reports/FrontOffice/CustomerDetails"));
const DailyFlashReports = lazy(() => import("./pages/Reports/FrontOffice/DailyFlashReports"));
const DataWiseCheckout = lazy(() => import("./pages/Reports/FrontOffice/DataWiseCheckout"));
const ExtraServiceReports = lazy(() => import("./pages/Reports/FrontOffice/ExtraServiceReports"));
const GuestChecking = lazy(() => import("./pages/Reports/FrontOffice/GuestChecking"));
const GuestCheckout = lazy(() => import("./pages/Reports/FrontOffice/GuestCheckout"));
const GuestHighBalance = lazy(() => import("./pages/Reports/FrontOffice/GuestHighBalance"));
const GuestDetailsReport = lazy(() => import("./pages/Reports/FrontOffice/GuestDetailsReport"));
const GuestRoomOccupancyProvisionalReports = lazy(() => import("./pages/Reports/FrontOffice/GuestRoomOccupancyProvisionalReports"));
const OccupyProvisional = lazy(() => import("./pages/Reports/FrontOffice/occupyProvisional"));
const PaymentCashierReports = lazy(() => import("./pages/Reports/FrontOffice/PaymentCashierReports"));
const PoliceReports = lazy(() => import("./pages/Reports/FrontOffice/PoliceReports"));
const RoomOccupancy = lazy(() => import("./pages/Reports/FrontOffice/RoomOccupancy"));
const MealPlanReport = lazy(() => import("./pages/Reports/FrontOffice/MealPlanReport"));
const RoomOtherPostServices = lazy(() => import("./pages/Reports/FrontOffice/RoomOtherPostServices"));
const RoomStatus = lazy(() => import("./pages/Reports/FrontOffice/RoomStatus"));
const TourismReports = lazy(() => import("./pages/Reports/FrontOffice/TourisumReports"));
const ExpectedArrivalReport = lazy(() => import("./pages/Reports/FrontOffice/ExpectedArrivalReport"));
const ExpectedArrivalView = lazy(() => import("./pages/Reports/FrontOffice/ExpectedArrivalView"));
const ExpectedDepartureReport = lazy(() => import("./pages/Reports/FrontOffice/ExpectedDepartureReport"));
const IncomeTaxReport = lazy(() => import("./pages/Reports/FrontOffice/IncomeTaxReport"));
const InHouseReport = lazy(() => import("./pages/Reports/FrontOffice/InHouseReport"));
const InHouseForeignerReport = lazy(() => import("./pages/Reports/FrontOffice/InHouseForeignerReport"));
const FoCashReport = lazy(() => import("./pages/Reports/FrontOffice/FoCashReport"));
const OtherChargesReport = lazy(() => import("./pages/Reports/FrontOffice/OtherChargesReport"));
const SegmentReport = lazy(() => import("./pages/Reports/FrontOffice/SegmentReport"));
const NationalityDetailsReport = lazy(() => import("./pages/Reports/FrontOffice/NationalityDetailsReport"));
const MarketingReport = lazy(() => import("./pages/Reports/FrontOffice/MarketingReport"));
const SalesSummaryReport = lazy(() => import("./pages/Reports/FrontOffice/SalesSummaryReport"));
const RoomChangesReport = lazy(() => import("./pages/Reports/FrontOffice/RoomChangesReport"));
const ImageCaptureReport = lazy(() => import("./pages/Reports/Guest/ImageCaptureReport"));
const LongStayGuestReport = lazy(() => import("./pages/Reports/Guest/LongStayGuestReport"));
const LinkedRoomDetailsReport = lazy(() => import("./pages/Reports/Guest/LinkedRoomDetailsReport"));
const GuestHistoryReport = lazy(() => import("./pages/Reports/Guest/GuestHistoryReport"));
const GuestList = lazy(() => import("./pages/Reports/Guest/GuestList"));
const ItemSaleAnalysisReports = lazy(() => import("./pages/Reports/FNB/ItemSaleAnalysisReports"));
const SalesRegister = lazy(() => import("./pages/Reports/FNB/SalesRegister"));
const ItemWiseSales = lazy(() => import("./pages/Reports/FNB/ItemWiseSales"));
const GroupWiseSaleSummary = lazy(() => import("./pages/Reports/FNB/GroupWiseSaleSummary"));
const CashierSale = lazy(() => import("./pages/Reports/FNB/CashierSale"));
const NCTransaction = lazy(() => import("./pages/Reports/FNB/NCTransaction"));
const PointOfSales = lazy(() => import("./pages/Reports/FNB/PointOFSales"));
const CancelledBillsReport = lazy(() => import("./pages/Reports/FNB/CancelledBillsReport"));
const StatisticReport = lazy(() => import("./pages/Reports/Audit/StatisticReport"));
const FlashReports = lazy(() => import("./pages/Reports/Audit/FlashReports"));
const PaymentCollectionRegister = lazy(() => import("./pages/Reports/Audit/PaymentCollectionRegister"));
const NightAuditSummaryReports = lazy(() => import("./pages/Reports/Audit/NightAuditSummaryReports"));
const NightAuditReport = lazy(() => import("./pages/Reports/Audit/NightAuditReport"));
const DailyBusiness = lazy(() => import("./pages/Reports/Audit/DailyBusiness"));
const DaySummaryReport = lazy(() => import("./pages/Reports/Audit/DaySummaryReport"));
const CashBookDetailReport = lazy(() => import("./pages/Reports/Audit/CashBookDetailReport"));
const DaySummaryMonthlyReport = lazy(() => import("./pages/Reports/Audit/DaySummaryMonthlyReport"));
const OccupancyForecast = lazy(() => import("./pages/Reports/Audit/OccupancyForecast"));
const DailyCheckinCheckOutReport = lazy(() => import("./pages/Reports/Audit/DailyCheckinCheckOutReport"));
const RateCheck = lazy(() => import("./pages/Reports/Audit/RateCheck"));
const RoomRevenue = lazy(() => import("./pages/Reports/Audit/RoomRevenue"));
const RoomTypewiseSales = lazy(() => import("./pages/Reports/Audit/RoomTypewiseSales"));
const RemoveTariffReport = lazy(() => import("./pages/Reports/Audit/RemoveTariffReport"));
const RoomChargesReport = lazy(() => import("./pages/Reports/Audit/RoomChargesReport"));
const RoomChargesMonthReport = lazy(() => import("./pages/Reports/Audit/RoomChargesMonthReport"));
const RoomHistoryReport = lazy(() => import("./pages/Reports/Audit/RoomHistoryReport"));
const NightAuditCheckList = lazy(() => import("./pages/Reports/Audit/NightAuditCheckList"));
const UnbilledTransactionReport = lazy(() => import("./pages/Reports/Audit/UnbilledTransactionReport"));
const ExecutiveSummaryReport = lazy(() => import("./pages/Reports/Audit/ExecutiveSummaryReport"));
const DaywiseSalesReport = lazy(() => import("./pages/Reports/Audit/DaywiseSalesReport"));
const AccessHistoryReport = lazy(() => import("./pages/Reports/Audit/AccessHistoryReport"));
const AuditTrailReport = lazy(() => import("./pages/Reports/Audit/AuditTrailReport"));
const LogReports = lazy(() => import("./pages/Reports/Audit/LogReports"));
const GuestLedgerReport = lazy(() => import("./pages/Reports/Audit/GuestLedgerReport"));
const StockLedger = lazy(() => import("./pages/Reports/Inventory/StockLedger"));
const StockLedgerSummary = lazy(() => import("./pages/Reports/Inventory/StockLedgerSummary"));
const ClosingStock = lazy(() => import("./pages/Reports/Inventory/ClosingStock"));
const DirectIssue = lazy(() => import("./pages/Inventory/DirectIssue"));
const DepartmentRequisition = lazy(() => import("./pages/Inventory/DepartmentRequisition"));
const DeptReqApproval = lazy(() => import("./pages/Inventory/DeptReqApproval"));
const PurchaseOrderEntry = lazy(() => import("./pages/Inventory/PurchaseOrderEntry"));
const PurchaseApproval = lazy(() => import("./pages/Inventory/PurchaseApproval"));
const GRNReceipts = lazy(() => import("./pages/Inventory/GRNReceipts"));
const DirectReceipts = lazy(() => import("./pages/Inventory/DirectReceipts"));
const IndentEntry = lazy(() => import("./pages/Inventory/IndentEntry"));
const IndentsApproval = lazy(() => import("./pages/Inventory/IndentsApproval"));
const IssuesToDo = lazy(() => import("./pages/Inventory/IssuesToDo"));
const IssueReturn = lazy(() => import("./pages/Inventory/IssueReturn"));
const GRNReturn = lazy(() => import("./pages/Inventory/GRNReturn"));
const AdjustmentStock = lazy(() => import("./pages/Inventory/AdjustmentStock"));
const CloseIndentPO = lazy(() => import("./pages/Inventory/CloseIndentPO"));
const MonthendClosingStock = lazy(() => import("./pages/Inventory/MonthendClosingStock"));
const StoreMaster = lazy(() => import("./pages/Inventory/masters/StoreMaster"));
const GroupMaster = lazy(() => import("./pages/Inventory/masters/GroupMaster"));
const UOMMaster = lazy(() => import("./pages/Inventory/masters/UOMMaster"));
const CostCentreMaster = lazy(() => import("./pages/Inventory/masters/CostCentre"));
const ItemMasterInv = lazy(() => import("./pages/Inventory/masters/ItemMaster"));
const TagItemsCostCentre = lazy(() => import("./pages/Inventory/masters/TagItemsCostCentre"));
const CostCentreItems = lazy(() => import("./pages/Inventory/masters/CostCentreItems"));
const SupplierMaster = lazy(() => import("./pages/Inventory/masters/SupplierMaster"));
const TaxCodeMaster = lazy(() => import("./pages/Inventory/masters/TaxCode"));
const TaxStructureMaster = lazy(() => import("./pages/Inventory/masters/TaxStructure"));
const BlockRoomReport = lazy(() => import("./pages/Reports/Housekeeping/BlockRoomReport"));
const MaintenanceRequestReport = lazy(() => import("./pages/Reports/Housekeeping/MaintenanceRequestReport"));
const RoomBoyCheckinList = lazy(() => import("./pages/Reports/Housekeeping/RoomBoyCheckinList"));
const HouseKeepingStatusAssigen = lazy(() => import("./pages/Reports/Housekeeping/HousekeepingStatus"));
const RoomClearanceStaffAllocation = lazy(() => import("./pages/Reports/Housekeeping/RoomClearanceStaffAllocation"));
const BlockRoomWiseHouseKeeping = lazy(() => import("./pages/Reports/Housekeeping/BlockRoomWise"));
const ClearRoomReport = lazy(() => import("./pages/Reports/Housekeeping/ClearRoomReport"));
const LaundrySalesReport = lazy(() => import("./pages/Reports/Housekeeping/LaundrySalesReport"));
const LaundryBillWiseReport = lazy(() => import("./pages/Reports/Housekeeping/LaundryBillWiseReport"));
const LaundryNCSalesReport = lazy(() => import("./pages/Reports/Housekeeping/LaundryNCSalesReport"));
const MinibarSalesReport = lazy(() => import("./pages/Reports/Housekeeping/MinibarSalesReport"));
const MinibarNCSalesReport = lazy(() => import("./pages/Reports/Housekeeping/MinibarNCSalesReport"));
const LaundryDuplicatePrint = lazy(() => import("./pages/Reports/Housekeeping/LaundryDuplicatePrint"));
const LaundryItemWiseSalesReport = lazy(() => import("./pages/Reports/Housekeeping/LaundryItemWiseSalesReport"));
const MinibarDuplicatePrint = lazy(() => import("./pages/Reports/Housekeeping/MinibarDuplicatePrint"));
const MinibarItemWiseSalesReport = lazy(() => import("./pages/Reports/Housekeeping/MinibarItemWiseSalesReport"));
const IssueReport = lazy(() => import("./pages/Reports/Inventory/IssueReport"));
const StockReport = lazy(() => import("./pages/Reports/Inventory/StockReport"));
const PurchaseReport = lazy(() => import("./pages/Reports/Inventory/PurchaseReport"));
const GRNReportSummary = lazy(() => import("./pages/Reports/Inventory/GRNReportSummary"));
const GRNReportDetailed = lazy(() => import("./pages/Reports/Inventory/GRNReportDetailed"));
const IndentReport = lazy(() => import("./pages/Reports/Inventory/IndentReport"));
const IssueConsolidateReport = lazy(() => import("./pages/Reports/Inventory/IssueConsolidateReport"));
const SupplierWiseItem = lazy(() => import("./pages/Reports/Inventory/SupplierWiseItem"));
const ItemMasterReport = lazy(() => import("./pages/Reports/Inventory/ItemMasterReport"));
const OutOfStockReport = lazy(() => import("./pages/Reports/Inventory/OutOfStockReport"));
const SupplierWiseReport = lazy(() => import("./pages/Reports/Inventory/SupplierWiseReport"));
const CostCentreConsum = lazy(() => import("./pages/Reports/Inventory/CostCentreConsum"));
const StorewiseIssue = lazy(() => import("./pages/Reports/Inventory/StorewiseIssue"));
const PurchaseFlash = lazy(() => import("./pages/Reports/Inventory/PurchaseFlash"));
const PhysicalStockReport = lazy(() => import("./pages/Reports/Inventory/PhysicalStockReport"));
const SupplierGRNReport = lazy(() => import("./pages/Reports/Inventory/SupplierGRNReport"));
const EPurchaseReport = lazy(() => import("./pages/Reports/Inventory/EPurchaseReport"));
const AdjustmentReport = lazy(() => import("./pages/Reports/Inventory/AdjustmentReport"));
const OutstandingPayable = lazy(() => import("./pages/Reports/BackOffice/OutstandingPayable"));
const BillWiseOutstanding = lazy(() => import("./pages/Reports/BackOffice/BillWiseOutstanding"));
const LedgerStatementReport = lazy(() => import("./pages/Reports/BackOffice/LedgerStatementReport"));
const AgingDebtorsDetails = lazy(() => import("./pages/Reports/BackOffice/AgingDebtorsDetails"));
const AgingDebtorsSummary = lazy(() => import("./pages/Reports/BackOffice/AgingDebtorsSummary"));
const CityLedgerDetails = lazy(() => import("./pages/Reports/BackOffice/CityLedgerDetails"));
const CityLedgerSummary = lazy(() => import("./pages/Reports/BackOffice/CityLedgerSummary"));
const CompanyLedger = lazy(() => import("./pages/Reports/BackOffice/CompanyLedger"));
const OutstandingReceivable = lazy(() => import("./pages/Reports/BackOffice/OutstandingReceivable"));
const AdvanceDetails = lazy(() => import("./pages/Reports/BackOffice/AdvanceDetails"));
const DayBookSummaryReports = lazy(() => import("./pages/Reports/BackOffice/DayBookSummaryReports"));
const BtoB = lazy(() => import("./pages/Reports/BackOffice/BtoB"));
const Pettycash = lazy(() => import("./pages/Reports/PettyCash/PettyCash"));
const BanquetBookingBook = lazy(() => import("./pages/Reports/Banquet/BanquetBookingBook"));
const FpWiseBanquetBooking = lazy(() => import("./pages/Reports/Banquet/FpWiseBanquetBookingReport"));
const LedgerAcc = lazy(() => import("./pages/accounting/master/LedgerMaster"));
const GroupAcc = lazy(() => import("./pages/accounting/master/AccountGroup"));
const VendorPayment = lazy(() => import("./pages/accounting/purchase/VendorPayment"));
const CustomerReceipt = lazy(() => import("./pages/accounting/receipts/CustomerReceipt"));
const AllReceipt = lazy(() => import("./pages/accounting/receipts/AllReceipts"));
const CashBook = lazy(() => import("./pages/accounting/banking/CashBook"));
const BankBook = lazy(() => import("./pages/accounting/banking/BankBook"));
const PaymentVoucher = lazy(() => import("./pages/accounting/voucher/PaymentVoucher"));
const ReceiptVoucher = lazy(() => import("./pages/accounting/voucher/ReceiptVoucher"));
const ContraVoucher = lazy(() => import("./pages/accounting/voucher/ContraVoucher"));
const Journal = lazy(() => import("./pages/accounting/voucher/JournalVoucher"));
const SingleVoucherEntry = lazy(() => import("./pages/accounting/voucher/SingleVoucherEntry"));
const LedgerStatement = lazy(() => import("./pages/accounting/reports/LedgerStatement"));
const DayBook = lazy(() => import("./pages/accounting/reports/DayBook"));
const SaleBillWise = lazy(() => import("./pages/accounting/reports/SaleBillWise"));
const Einvoice = lazy(() => import("./pages/accounting/gst/EInvoice"));
const UploadEinvoice = lazy(() => import("./pages/accounting/gst/UploadEInvoice"));
const CustomerMaster = lazy(() => import("./pages/accounting/master/CustomerMaster"));
const VendorMaster = lazy(() => import("./pages/accounting/master/VendorMaster"));
const GSTMaster = lazy(() => import("./pages/accounting/master/GSTMaster"));
const FinancialYear = lazy(() => import("./pages/accounting/master/FinancialYear"));
const SalesInvoice = lazy(() => import("./pages/accounting/sales/SalesInvoice"));
const CorporateInvoice = lazy(() => import("./pages/accounting/sales/CorporateInvoice"));
const InvoiceList = lazy(() => import("./pages/accounting/sales/InvoiceList"));
const PurchaseInvoice = lazy(() => import("./pages/accounting/purchase/PurchaseInvoice"));
const PurchaseReturn = lazy(() => import("./pages/accounting/purchase/PurchaseReturn"));
const PurchaseList = lazy(() => import("./pages/accounting/purchase/PurchaseList"));
const VoucherList = lazy(() => import("./pages/accounting/voucher/VoucherList"));
const AdvanceReceipt = lazy(() => import("./pages/accounting/receipts/AdvanceReceipt"));
const BankReconciliation = lazy(() => import("./pages/accounting/banking/BankReconciliation"));
const TrialBalance = lazy(() => import("./pages/accounting/reports/TrialBalance"));
const ProfitLoss = lazy(() => import("./pages/accounting/reports/ProfitLoss"));
const BalanceSheet = lazy(() => import("./pages/accounting/reports/BalanceSheet"));
const OutstandingReport = lazy(() => import("./pages/accounting/reports/OutstandingReport"));
const AgingReport = lazy(() => import("./pages/accounting/reports/AgingReport"));
const RefundsReport = lazy(() => import("./pages/accounting/reports/RefundsReport"));
const GSTR1Report = lazy(() => import("./pages/accounting/gst/GSTR1Report"));
const GSTR3BReport = lazy(() => import("./pages/accounting/gst/GSTR3BReport"));
const HSNReport = lazy(() => import("./pages/accounting/gst/HSNReport"));
const AssetMaster = lazy(() => import("./pages/accounting/assets/AssetMaster"));
const DepreciationEntry = lazy(() => import("./pages/accounting/assets/DepreciationEntry"));
const VoucherNumbering = lazy(() => import("./pages/accounting/settings/VoucherNumbering"));
const RolePermission = lazy(() => import("./pages/accounting/settings/RolePermission"));
const YearLock = lazy(() => import("./pages/accounting/settings/YearLock"));
const OTABookings = lazy(() => import("./pages/ChannelManager/OTABookings"));
const STAAHConfig = lazy(() => import("./pages/ChannelManager/STAAHConfig"));
const PerformanceReport = lazy(() => import("./pages/ChannelManager/PerformanceReport"));
const SyncStatus = lazy(() => import("./pages/ChannelManager/SyncStatus"));
const PayrollPage = lazy(() => import("./pages/module/PayrollPage"));
const EmployeePayroll = lazy(() => import("./pages/Payroll/EmployeePayroll"));
const SalaryStructurePage = lazy(() => import("./pages/Payroll/SalaryStructure"));
const ProcessPayroll = lazy(() => import("./pages/Payroll/ProcessPayroll"));
const PayslipsPage = lazy(() => import("./pages/Payroll/Payslips"));
const AttendanceSummaryPage = lazy(() => import("./pages/Payroll/AttendanceSummary"));
const LeaveManagementPage = lazy(() => import("./pages/Payroll/LeaveManagement"));
const OvertimeApprovalPage = lazy(() => import("./pages/Payroll/OvertimeApproval"));
const RosterSchedulingPage = lazy(() => import("./pages/Payroll/RosterScheduling"));
const FullFinalSettlementPage = lazy(() => import("./pages/Payroll/FullFinalSettlement"));
const MyProfilePage = lazy(() => import("./pages/Payroll/ess/MyProfile"));
const MyPayslipsPage = lazy(() => import("./pages/Payroll/ess/MyPayslips"));
const MyLeaveOvertimePage = lazy(() => import("./pages/Payroll/ess/MyLeaveOvertime"));
const MyRosterPage = lazy(() => import("./pages/Payroll/ess/MyRoster"));
const MyTeamPage = lazy(() => import("./pages/Payroll/mss/MyTeam"));

// Backoffice — Property

// Backoffice — Masters

// Backoffice — Settings
// import TableColumnListCRM from "./pages/Backoffice/setting/TableColumnListCRM";
// import StoreSerialNoSetting from "./pages/Backoffice/setting/StoreSerialNoSetting";

// Backoffice — CommonMaster

// Backoffice — QRMenuSetting

// Backoffice — FNB

// Backoffice — Room

// Backoffice — Currency

// Backoffice — Nc

// Backoffice — Report

// Backoffice — Complain

// Backoffice — Production

// Backoffice — User Management

// Backoffice — Accounts

// Backoffice — Membership
// import MemberShipSetting from "./pages/Backoffice/membership/MemberShipSetting";

// Reservation

// Reservation-Frontoffice


// Petty Cash Imports

// Other Hotel Imports

// Club Imports


// Housekeeping

// Housekeeping Masters (under Backoffice)

// Housekeeping-Reports

//Housekeeping settings



// POS
// import Banquet from "./pages/Pos/Restaurant/Banquet";

// Inventory

// Inventory Reports

// Reports

// Settings
// import FinancialYear from "./pages/Pos/Settings/FinancialYear";

// Purchase

// Banquet Ops

// Reports - Reservation

// Reports - Front Office

// Reports - F&B

// Reports - Audit

// Reports - Inventory

// Inventory Transactions

// Inventory — Masters

// Reports - Housekeeping

// Reports - Back Office

// Reports - Petty Cash

// Reports - Banquet

// Accounting Imports
// Sales & Purchase
// Receipts
// Banking
// Voucher
// Reports
// GST









// Accounting Additional Imports










// Payroll

export default function App() {
  return (
    <BrowserRouter>
      <WebSocketProvider>
        <Toaster position="top-right" reverseOrder={false} />
        <Suspense fallback={<div style={{ display: "flex", alignItems: "center", justifyContent: "center", height: "100vh", fontSize: "1rem", color: Colors.slate500 }}>Loading...</div>}>
        <Routes>
          {/* ============================================ */}
        {/* AUTHENTICATION ROUTES */}
        {/* ============================================ */}
        <Route path="/" element={<Navigate to="/login" replace />} />
        <Route path="/login" element={<Login />} />

        {/* ============================================ */}
        {/* DASHBOARD HOME */}
        {/* ============================================ */}
        <Route
          path="/dashboard"
          element={
            <ProtectedRoute>
              <Dashboard />
            </ProtectedRoute>
          }
        />
        <Route path="/module/:id" element={<ModulePage />} />

        {/* ============================================ */}
        {/* RESERVATION MODULE */}
        {/* ============================================ */}
        <Route
          path="/dashboard/reservation"
          element={
            <ProtectedRoute>
              <ReservationPage />
            </ProtectedRoute>
          }
        />

        {/* Reservation Operations */}
        <Route path="/reservation/new" element={<ProtectedRoute><NewReservation /></ProtectedRoute>} />
        <Route path="/reservation/confirmation-voucher/:id" element={<ProtectedRoute><ConfirmationVoucher /></ProtectedRoute>} />
        <Route path="/reservation/booking-details" element={<ProtectedRoute><ReservationDetails /></ProtectedRoute>} />
        <Route path="/reservation/StatusView" element={<ProtectedRoute><ReservationStatusView /></ProtectedRoute>} />
        <Route path="/reservation/CancelReservationList" element={<ProtectedRoute><CancelReservationList /></ProtectedRoute>} />
        <Route path="/reservation/ReservationCalendar" element={<ProtectedRoute><ReservationCalendar /></ProtectedRoute>} />
        <Route path="/reservation/AdvanceDeposit" element={<ProtectedRoute><AdvanceDeposit /></ProtectedRoute>} />
        <Route path="/reservation/ReturnPaidup" element={<ProtectedRoute><ReturnPaidup /></ProtectedRoute>} />
        <Route path="/reservation/NoShowRoomReport" element={<ProtectedRoute><NoShowRoomReport /></ProtectedRoute>} />
        <Route path="/reservation/BookingSheetAccounts" element={<ProtectedRoute><BookingSheetAccounts /></ProtectedRoute>} />
        <Route path="/reservation/cross-property-availability" element={<ProtectedRoute><CrossPropertyAvailability /></ProtectedRoute>} />
        <Route path="/reservation/guest-requests" element={<ProtectedRoute><GuestRequests /></ProtectedRoute>} />
        <Route path="/dashboard/guests" element={<ProtectedRoute><GuestMaster /></ProtectedRoute>} />
        <Route path="/engineering/pm-schedules" element={<ProtectedRoute><PMSchedules /></ProtectedRoute>} />
        <Route path="/dashboard/operations/shift-logbook" element={<ProtectedRoute><ShiftLogbook /></ProtectedRoute>} />

        {/* ============================================ */}
        {/* FRONT OFFICE MODULE */}
        {/* ============================================ */}
        <Route
          path="/dashboard/frontdesk"
          element={
            <ProtectedRoute>
              <FrontdeskPage />
            </ProtectedRoute>
          }
        />

        {/* Check-in Operations */}
        <Route path="/Reservation/frontdesk/PreRegCard" element={<ProtectedRoute><PreRegCard /></ProtectedRoute>} />
        <Route path="/Reservation/frontoffice/CheckinGuest" element={<ProtectedRoute><CheckInGuest /></ProtectedRoute>} />
        <Route path="/Reservation/frontoffice/DirectCheckInGuest" element={<ProtectedRoute><DirectCheckInGuest /></ProtectedRoute>} />
        <Route path="/Reservation/frontoffice/CheckInGuestDetails" element={<ProtectedRoute><CheckInGuestDetails /></ProtectedRoute>} />
        <Route path="/Reservation/frontoffice/PaxCheckin" element={<ProtectedRoute><PaxCheckin /></ProtectedRoute>} />

        {/* Check-out Operations */}
        <Route path="/Reservation/frontoffice/CheckOutGuest" element={<ProtectedRoute><CheckOutGuest /></ProtectedRoute>} />
        <Route path="/Reservation/frontoffice/GuestCheckoutExtend" element={<ProtectedRoute><GuestCheckoutExtend /></ProtectedRoute>} />
        <Route path="/Reservation/frontoffice/ReturnPaidup" element={<ProtectedRoute><ReturnPaidup /></ProtectedRoute>} />
        <Route path="/Reservation/frontoffice/post-charges" element={<ProtectedRoute><PostCharges /></ProtectedRoute>} />
        <Route path="/Reservation/frontoffice/posting-mismatch" element={<ProtectedRoute><PostingMismatch /></ProtectedRoute>} />
        <Route path="/Reservation/frontoffice/Checkoutdetails" element={<ProtectedRoute><CheckoutGuestdetails /></ProtectedRoute>} />
        <Route path="/Reservation/frontoffice/booking-linked-unlinked" element={<ProtectedRoute><BookingLink /></ProtectedRoute>} />
        <Route path="/Reservation/frontoffice/settlement" element={<ProtectedRoute><Settlement /></ProtectedRoute>} />
        <Route path="/Reservation/frontoffice/room-shifting" element={<ProtectedRoute><RoomShifting /></ProtectedRoute>} />
        <Route path="/frontdesk/reservation/print-bill/:id" element={<PrintBill />} />
        <Route path="/dashboard/frontdesk/night-audit" element={<ProtectedRoute><NightAuditProcess /></ProtectedRoute>} />
        <Route path="/dashboard/reports/ReservationAdvancePendingFO" element={<ProtectedRoute><ReservationAdvancePendingFO /></ProtectedRoute>} />
        {/* Petty Cash Routes */}
        <Route path="/petty-cash/payment-expense" element={<ProtectedRoute><PaymentExpense /></ProtectedRoute>} />
        <Route path="/petty-cash/receipt" element={<ProtectedRoute><Receipt /></ProtectedRoute>} />
        <Route path="/petty-cash/expense-summary-report" element={<ProtectedRoute><ExpenseSummaryReport /></ProtectedRoute>} />
        <Route path="/petty-cash/expense-head-master" element={<ProtectedRoute><ExpenseHeadMaster /></ProtectedRoute>} />
        <Route path="/petty-cash/receive-head-master" element={<ProtectedRoute><ReceiveHeadMaster /></ProtectedRoute>} />

        {/* Other Hotel Routes */}
        <Route path="/other-hotel/reservation" element={<ProtectedRoute><OtherHotelReservation /></ProtectedRoute>} />
        <Route path="/other-hotel/stars-request" element={<ProtectedRoute><StarsRequest /></ProtectedRoute>} />
        <Route path="/other-hotel/booking-details" element={<ProtectedRoute><OtherHotelBookingDetails /></ProtectedRoute>} />

        {/* Club Routes */}
        <Route path="/club/member-registration" element={<ProtectedRoute><MemberRegistration /></ProtectedRoute>} />
        <Route path="/club/membership-details" element={<ProtectedRoute><MembershipDetails /></ProtectedRoute>} />
        <Route path="/club/membership-allotment" element={<ProtectedRoute><MembershipAllotment /></ProtectedRoute>} />
        <Route path="/club/allotment-details" element={<ProtectedRoute><AllotmentDetails /></ProtectedRoute>} />
        <Route path="/club/membership-enrollment" element={<ProtectedRoute><MembershipEnrollment /></ProtectedRoute>} />

        {/* Front Office Reports & Tools */}
        <Route path="/Reservation/frontoffice/CancelBookingDetails" element={<ProtectedRoute><CancelBookingDetails /></ProtectedRoute>} />
        <Route path="/Reservation/frontoffice/RoomCalendar" element={<ProtectedRoute><RoomCalendar /></ProtectedRoute>} />
        <Route path="/Reservation/frontoffice/LinkedUnlinkedReport" element={<ProtectedRoute><LinkedUnlinkedReport /></ProtectedRoute>} />
        <Route path="/reports/front-office/in-house" element={<InHouseReport />} />
        <Route path="/reports/guest/image-capture" element={<ImageCaptureReport />} />
        <Route path="/reports/guest/long-stay" element={<LongStayGuestReport />} />
        <Route path="/reports/guest/linked-room-details" element={<LinkedRoomDetailsReport />} />
        <Route path="/reports/guest/guest-history" element={<ProtectedRoute><GuestHistoryReport /></ProtectedRoute>} />
        <Route path="/reports/guest/guest-list" element={<ProtectedRoute><GuestList /></ProtectedRoute>} />
        <Route path="/Reservation/frontoffice/Calendar" element={<ProtectedRoute><Calendar /></ProtectedRoute>} />

        {/* ============================================ */}
        {/* HOUSEKEEPING MODULE */}
        {/* ============================================ */}
        <Route
          path="/dashboard/housekeeping"
          element={
            <ProtectedRoute>
              <HousekeepingPage />
            </ProtectedRoute>
          }
        />

        {/* Housekeeping Operations */}
        <Route path="/dashboard/Housekeeping/RoomStatusBoard" element={<ProtectedRoute><RoomStatusBoard /></ProtectedRoute>} />
        <Route path="/dashboard/Housekeeping/AssignTasks" element={<ProtectedRoute><AssignTasks /></ProtectedRoute>} />
        <Route path="/dashboard/Housekeeping/CleaningSchedule" element={<ProtectedRoute><CleaningSchedule /></ProtectedRoute>} />
        <Route path="/dashboard/Housekeeping/RoomInspection" element={<ProtectedRoute><RoomInspection /></ProtectedRoute>} />
        <Route path="/dashboard/Housekeeping/TurnDownService" element={<ProtectedRoute><TurnDownService /></ProtectedRoute>} />
        <Route path="/dashboard/Housekeeping/DeepCleaningPlanner" element={<ProtectedRoute><DeepCleaningPlanner /></ProtectedRoute>} />
        <Route path="/dashboard/Housekeeping/LostAndFound" element={<ProtectedRoute><LostAndFound /></ProtectedRoute>} />
        <Route path="/dashboard/Housekeeping/MaintenanceRequests" element={<ProtectedRoute><MaintenanceRequests /></ProtectedRoute>} />
        <Route path="/dashboard/Housekeeping/LinenInventory" element={<ProtectedRoute><LinenInventory /></ProtectedRoute>} />
        <Route path="/dashboard/Housekeeping/MinibarRefill" element={<ProtectedRoute><MinibarRefill /></ProtectedRoute>} />
        <Route path="/dashboard/Housekeeping/RoomCalendar" element={<ProtectedRoute><RoomCalendar /></ProtectedRoute>} />
        <Route path="/dashboard/Housekeeping/LaundryReceipt" element={<ProtectedRoute><LaundryReceipt /></ProtectedRoute>} />
        <Route path="/dashboard/Housekeeping/LaundryBilling" element={<ProtectedRoute><LaundryBilling /></ProtectedRoute>} />
        <Route path="/dashboard/Housekeeping/LaundrySettlement" element={<ProtectedRoute><LaundrySettlement /></ProtectedRoute>} />
        <Route path="/dashboard/Housekeeping/MinibarBilling" element={<ProtectedRoute><MinibarBilling /></ProtectedRoute>} />
        <Route path="/dashboard/Housekeeping/MinibarSettlement" element={<ProtectedRoute><MinibarSettlement /></ProtectedRoute>} />
        <Route path="/dashboard/Housekeeping/LaundererIssue" element={<ProtectedRoute><LaundererIssue /></ProtectedRoute>} />
        <Route path="/dashboard/Housekeeping/ReturnIssue" element={<ProtectedRoute><ReturnIssue /></ProtectedRoute>} />
        <Route path="/dashboard/Housekeeping/Handover" element={<ProtectedRoute><Handover /></ProtectedRoute>} />

        {/* Housekeeping Masters — accessed via Backoffice sidebar */}
        <Route path="/dashboard/backoffice/hk-master/StaffMaster" element={<ProtectedRoute><HKStaffMaster /></ProtectedRoute>} />
        <Route path="/dashboard/backoffice/hk-master/DeliveryTypes" element={<ProtectedRoute><HKDeliveryTypes /></ProtectedRoute>} />
        <Route path="/dashboard/backoffice/hk-master/ServiceTypes" element={<ProtectedRoute><HKServiceTypes /></ProtectedRoute>} />
        <Route path="/dashboard/backoffice/hk-master/Location" element={<ProtectedRoute><HKLocation /></ProtectedRoute>} />
        <Route path="/dashboard/backoffice/hk-master/LaundryItemMaster" element={<ProtectedRoute><HKLaundryItemMaster /></ProtectedRoute>} />
        <Route path="/dashboard/backoffice/hk-master/MinibarItemMaster" element={<ProtectedRoute><HKMinibarItemMaster /></ProtectedRoute>} />
        <Route path="/dashboard/backoffice/hk-master/LaundererMaster" element={<ProtectedRoute><HKLaundererMaster /></ProtectedRoute>} />

        {/* Housekeeping Reports */}
        <Route path="/dashboard/Housekeeping/Reports/Productivity" element={<ProtectedRoute><Productivity /></ProtectedRoute>} />
        <Route path="/dashboard/Housekeeping/Reports/StatusHistory" element={<ProtectedRoute><StatusHistory /></ProtectedRoute>} />
        <Route path="/dashboard/Housekeeping/Reports/LostFoundLog" element={<ProtectedRoute><LostFoundLog /></ProtectedRoute>} />
        <Route path="/dashboard/Housekeeping/Reports/MaterialsUsage" element={<ProtectedRoute><MaterialsUsage /></ProtectedRoute>} />

        {/* setting */}
        <Route path="/dashboard/Housekeeping/Settings/TaskTypes" element={<ProtectedRoute><TaskTypes /></ProtectedRoute>} />
        <Route path="/dashboard/Housekeeping/Settings/Statuses" element={<ProtectedRoute><Statuses /></ProtectedRoute>} />
        <Route path="/dashboard/Housekeeping/Settings/Checklists" element={<ProtectedRoute><Checklists /></ProtectedRoute>} />
        <Route path="/dashboard/Housekeeping/Settings/Staff" element={<ProtectedRoute><StaffTeams /></ProtectedRoute>} />

        {/* ============================================ */}
        {/* POS (POINT OF SALE) MODULE */}
        {/* ============================================ */}
        <Route
          path="/dashboard/pos"
          element={
            <ProtectedRoute>
              <PosPage />
            </ProtectedRoute>
          }
        />

        {/* POS Operations */}
        <Route path="/dashboard/pos/restaurant/availability-dashboard" element={<ProtectedRoute><TablePlan /></ProtectedRoute>} />
        <Route path="/dashboard/pos/restaurant/downsend" element={<ProtectedRoute><Downsend /></ProtectedRoute>} />
        {/* <Route path="/dashboard/pos/restaurant/banquet" element={<ProtectedRoute><Banquet /></ProtectedRoute>} /> */}
        <Route path="/dashboard/pos/bar" element={<ProtectedRoute><Bar /></ProtectedRoute>} />
        <Route path="/dashboard/pos/Roomservice" element={<ProtectedRoute><RoomService /></ProtectedRoute>} />
        <Route path="/dashboard/Pos/Housekeeping/Laundry" element={<ProtectedRoute><Laundry /></ProtectedRoute>} />
        <Route path="/dashboard/pos/banquet/swargtrustify" element={<ProtectedRoute><Swargtrustify /></ProtectedRoute>} />

        {/* POS Reports */}
        <Route path="/dashboard/pos/report/tally" element={<ProtectedRoute><TallyReport /></ProtectedRoute>} />
        <Route path="/dashboard/pos/report/bill" element={<ProtectedRoute><BillReport /></ProtectedRoute>} />
        <Route path="/dashboard/pos/report/fnb-summary" element={<ProtectedRoute><FnbSummary /></ProtectedRoute>} />
        <Route path="/dashboard/pos/report/order-summary" element={<ProtectedRoute><OrderSummary /></ProtectedRoute>} />

        {/* POS Settings */}
        <Route path="/dashboard/pos/settings/printer-settings" element={<ProtectedRoute><PrinterSettings /></ProtectedRoute>} />
        <Route path="/dashboard/pos/settings/financial-year" element={<ProtectedRoute><FinancialYear /></ProtectedRoute>} />
        <Route path="/dashboard/pos/settings/printer-exe" element={<ProtectedRoute><PrinterExe /></ProtectedRoute>} />

        {/* POS Purchase Management */}
        <Route path="/dashboard/pos/purchase/category" element={<ProtectedRoute><Category /></ProtectedRoute>} />
        <Route path="/dashboard/pos/purchase/sub-category" element={<ProtectedRoute><SubCategory /></ProtectedRoute>} />
        <Route path="/dashboard/pos/purchase/item-master" element={<ProtectedRoute><ItemMaster /></ProtectedRoute>} />
        <Route path="/dashboard/pos/purchase/purchase-details" element={<ProtectedRoute><PurchaseDetails /></ProtectedRoute>} />
        <Route path="/dashboard/pos/purchase/purchase-challan" element={<ProtectedRoute><PurchaseChallan /></ProtectedRoute>} />
        <Route path="/dashboard/pos/purchase/purchase-order" element={<ProtectedRoute><PurchaseOrder /></ProtectedRoute>} />
        <Route path="/dashboard/pos/purchase/transaction-report" element={<ProtectedRoute><TransactionReport /></ProtectedRoute>} />
        <Route path="/dashboard/pos/purchase/add-supplier" element={<ProtectedRoute><AddSupplier /></ProtectedRoute>} />
        <Route path="/dashboard/pos/purchase/add-purchase-requisition" element={<ProtectedRoute><PurchaseRequisition /></ProtectedRoute>} />

        {/* ============================================ */}
        {/* INVENTORY MODULE */}
        {/* ============================================ */}
        <Route
          path="/dashboard/inventory"
          element={
            <ProtectedRoute>
              <InventoryPage />
            </ProtectedRoute>
          }
        />

        {/* MATERIAL MODULE */}
        {/* ============================================ */}
        <Route
          path="/dashboard/material"
          element={
            <ProtectedRoute>
              <MaterialPage />
            </ProtectedRoute>
          }
        />

        {/* Inventory Operations */}
        <Route path="/dashboard/pos/inventory/issue-material" element={<ProtectedRoute><IssueMaterial /></ProtectedRoute>} />
        <Route path="/dashboard/pos/inventory/indent-details" element={<ProtectedRoute><IndentDetails /></ProtectedRoute>} />
        <Route path="/dashboard/pos/inventory/stock-manual-consumption" element={<ProtectedRoute><StockManualConsumption /></ProtectedRoute>} />
        <Route path="/dashboard/pos/inventory/stock-adjustment" element={<ProtectedRoute><StockAdjustment /></ProtectedRoute>} />
        <Route path="/dashboard/pos/inventory/stock-transfer" element={<ProtectedRoute><StockTransfer /></ProtectedRoute>} />
        <Route path="/dashboard/pos/inventory/department-op-stock" element={<ProtectedRoute><DepartmentOpStock /></ProtectedRoute>} />
        <Route path="/dashboard/pos/inventory/stock-verification" element={<ProtectedRoute><StockVerification /></ProtectedRoute>} />
        <Route path="/dashboard/pos/inventory/stock-detail" element={<ProtectedRoute><InventoryStockDetail /></ProtectedRoute>} />

        {/* Inventory Reports */}
        <Route path="/dashboard/pos/inventory-report/item-inventory" element={<ProtectedRoute><ItemInventory /></ProtectedRoute>} />
        <Route path="/dashboard/pos/inventory-report/issue-transaction" element={<ProtectedRoute><IssueTransaction /></ProtectedRoute>} />
        <Route path="/dashboard/pos/inventory-report/non-moving-item" element={<ProtectedRoute><NonMovingItems /></ProtectedRoute>} />
        <Route path="/dashboard/pos/inventory-report/stock-manual-consumption" element={<ProtectedRoute><ManualConsumptionReport /></ProtectedRoute>} />

        {/* ============================================ */}
        {/* BANQUET MODULE */}
        {/* ============================================ */}
        <Route
          path="/dashboard/banquet"
          element={
            <ProtectedRoute>
              <BanquetPage />
            </ProtectedRoute>
          }
        />

        {/* Banquet Operations */}
        <Route path="/dashboard/banquet/booking-detail" element={<ProtectedRoute><BookingDetail /></ProtectedRoute>} />
        <Route path="/dashboard/banquet/calendar" element={<ProtectedRoute><BanquetCalendar /></ProtectedRoute>} />
        <Route path="/dashboard/banquet/tally-report" element={<ProtectedRoute><BanquetTallyReport /></ProtectedRoute>} />
        <Route path="/dashboard/banquet/hall-availability" element={<ProtectedRoute><HallAvailability /></ProtectedRoute>} />
        <Route path="/dashboard/banquet/enquiries" element={<ProtectedRoute><BanquetEnquiries /></ProtectedRoute>} />
        <Route path="/dashboard/banquet/bookings" element={<ProtectedRoute><BanquetBookings /></ProtectedRoute>} />
        <Route path="/dashboard/banquet/invoices" element={<ProtectedRoute><BanquetInvoices /></ProtectedRoute>} />
        <Route path="/dashboard/banquet/settlements" element={<ProtectedRoute><BanquetSettlements /></ProtectedRoute>} />
        <Route path="/dashboard/banquet/reports" element={<ProtectedRoute><BanquetRevenueReports /></ProtectedRoute>} />
        <Route path="/dashboard/banquet/settings/halls" element={<ProtectedRoute><BanquetHalls /></ProtectedRoute>} />
        <Route path="/dashboard/banquet/settings/menus" element={<ProtectedRoute><BanquetMenus /></ProtectedRoute>} />
        <Route path="/dashboard/banquet/kpi-reports" element={<ProtectedRoute><BanquetKpiReports /></ProtectedRoute>} />


        {/* ============================================ */}
        {/* ACCOUNTING MODULE */}
        {/* ============================================ */}
        <Route
          path="/dashboard/accounting"
          element={
            <ProtectedRoute>
              <AccountingPage />
            </ProtectedRoute>
          }
        />

        {/* Accounting Masters */}
        <Route path="/dashboard/accounting/master/ledger" element={<ProtectedRoute><LedgerAcc /></ProtectedRoute>} />
        <Route path="/dashboard/accounting/master/group" element={<ProtectedRoute><GroupAcc /></ProtectedRoute>} />
        <Route path="/dashboard/accounting/master/customer" element={<ProtectedRoute><CustomerMaster /></ProtectedRoute>} />
        <Route path="/dashboard/accounting/master/vendor" element={<ProtectedRoute><VendorMaster /></ProtectedRoute>} />
        <Route path="/dashboard/accounting/master/gst" element={<ProtectedRoute><GSTMaster /></ProtectedRoute>} />
        <Route path="/dashboard/accounting/master/financial-year" element={<ProtectedRoute><FinancialYear /></ProtectedRoute>} />

        {/* Accounting Sales */}
        <Route path="/dashboard/accounting/sales/invoice" element={<ProtectedRoute><SalesInvoice /></ProtectedRoute>} />
        <Route path="/dashboard/accounting/sales/corporate-invoice" element={<ProtectedRoute><CorporateInvoice /></ProtectedRoute>} />
        <Route path="/dashboard/accounting/sales/list" element={<ProtectedRoute><InvoiceList /></ProtectedRoute>} />

        {/* Accounting Purchase */}
        <Route path="/dashboard/accounting/purchase/vendor-payment" element={<ProtectedRoute><VendorPayment /></ProtectedRoute>} />
        <Route path="/dashboard/accounting/purchase/invoice" element={<ProtectedRoute><PurchaseInvoice /></ProtectedRoute>} />
        <Route path="/dashboard/accounting/purchase/return" element={<ProtectedRoute><PurchaseReturn /></ProtectedRoute>} />
        <Route path="/dashboard/accounting/purchase/list" element={<ProtectedRoute><PurchaseList /></ProtectedRoute>} />

        {/* Accounting Receipts */}
        <Route path="/dashboard/accounting/receipts/customer" element={<ProtectedRoute><CustomerReceipt /></ProtectedRoute>} />
        <Route path="/dashboard/accounting/receipts/advance" element={<ProtectedRoute><AdvanceReceipt /></ProtectedRoute>} />
        <Route path="/dashboard/accounting/receipts/all" element={<ProtectedRoute><AllReceipt /></ProtectedRoute>} />

        {/* Accounting Vouchers */}
        <Route path="/dashboard/accounting/voucher/payment" element={<ProtectedRoute><PaymentVoucher /></ProtectedRoute>} />
        <Route path="/dashboard/accounting/voucher/receipt" element={<ProtectedRoute><ReceiptVoucher /></ProtectedRoute>} />
        <Route path="/dashboard/accounting/voucher/contra" element={<ProtectedRoute><ContraVoucher /></ProtectedRoute>} />
        <Route path="/dashboard/accounting/voucher/journal" element={<ProtectedRoute><Journal /></ProtectedRoute>} />
        <Route path="/dashboard/accounting/voucher/single-entry" element={<ProtectedRoute><SingleVoucherEntry /></ProtectedRoute>} />
        <Route path="/dashboard/accounting/voucher/list" element={<ProtectedRoute><VoucherList /></ProtectedRoute>} />

        {/* Accounting Banking */}
        <Route path="/dashboard/accounting/banking/cash-book" element={<ProtectedRoute><CashBook /></ProtectedRoute>} />
        <Route path="/dashboard/accounting/banking/bank-book" element={<ProtectedRoute><BankBook /></ProtectedRoute>} />
        <Route path="/dashboard/accounting/banking/reconciliation" element={<ProtectedRoute><BankReconciliation /></ProtectedRoute>} />

        {/* Accounting Reports */}
        <Route path="/dashboard/accounting/reports/day-book" element={<ProtectedRoute><DayBook /></ProtectedRoute>} />
        <Route path="/dashboard/accounting/reports/ledger-statement" element={<ProtectedRoute><LedgerStatement /></ProtectedRoute>} />
        <Route path="/dashboard/accounting/reports/sale-bill-wise" element={<ProtectedRoute><SaleBillWise /></ProtectedRoute>} />
        <Route path="/dashboard/accounting/reports/trial-balance" element={<ProtectedRoute><TrialBalance /></ProtectedRoute>} />
        <Route path="/dashboard/accounting/reports/profit-loss" element={<ProtectedRoute><ProfitLoss /></ProtectedRoute>} />
        <Route path="/dashboard/accounting/reports/balance-sheet" element={<ProtectedRoute><BalanceSheet /></ProtectedRoute>} />
        <Route path="/dashboard/accounting/reports/outstanding" element={<ProtectedRoute><OutstandingReport /></ProtectedRoute>} />
        <Route path="/dashboard/accounting/reports/aging" element={<ProtectedRoute><AgingReport /></ProtectedRoute>} />
        <Route path="/dashboard/accounting/reports/refunds" element={<ProtectedRoute><RefundsReport /></ProtectedRoute>} />

        {/* E-Invoice & GST */}
        <Route path="/dashboard/accounting/gst/e-invoice" element={<ProtectedRoute><Einvoice /></ProtectedRoute>} />
        <Route path="/dashboard/accounting/gst/upload" element={<ProtectedRoute><UploadEinvoice /></ProtectedRoute>} />
        <Route path="/dashboard/accounting/gst/gstr1" element={<ProtectedRoute><GSTR1Report /></ProtectedRoute>} />
        <Route path="/dashboard/accounting/gst/gstr3b" element={<ProtectedRoute><GSTR3BReport /></ProtectedRoute>} />
        <Route path="/dashboard/accounting/gst/hsn" element={<ProtectedRoute><HSNReport /></ProtectedRoute>} />

        {/* Assets */}
        <Route path="/dashboard/accounting/assets/master" element={<ProtectedRoute><AssetMaster /></ProtectedRoute>} />
        <Route path="/dashboard/accounting/assets/depreciation" element={<ProtectedRoute><DepreciationEntry /></ProtectedRoute>} />

        {/* Settings */}
        <Route path="/dashboard/accounting/settings/numbering" element={<ProtectedRoute><VoucherNumbering /></ProtectedRoute>} />
        <Route path="/dashboard/accounting/settings/roles" element={<ProtectedRoute><RolePermission /></ProtectedRoute>} />
        <Route path="/dashboard/accounting/settings/year-lock" element={<ProtectedRoute><YearLock /></ProtectedRoute>} />

        {/* ============================================ */}
        {/* BACK OFFICE MODULE */}
        {/* ============================================ */}
        <Route
          path="/dashboard/backoffice"
          element={
            <ProtectedRoute>
              <BackofficePage />
            </ProtectedRoute>
          }
        />

        {/* --- Property & Core Masters --- */}
        <Route path="/dashboard/backoffice/masters/property" element={<ProtectedRoute><PropertyProfile /></ProtectedRoute>} />
        <Route path="/dashboard/backoffice/masters/property-information" element={<ProtectedRoute><PropertyInformation /></ProtectedRoute>} />
        <Route path="/dashboard/backoffice/masters/property-information-app" element={<ProtectedRoute><PropertyInformationApp /></ProtectedRoute>} />
        <Route path="/dashboard/backoffice/masters/salesperson" element={<ProtectedRoute><SalesPerson /></ProtectedRoute>} />

        {/* --- Location Masters --- */}
        <Route path="/dashboard/backoffice/masters/state" element={<ProtectedRoute><StateMaster /></ProtectedRoute>} />
        <Route path="/dashboard/backoffice/masters/city" element={<ProtectedRoute><CityMaster /></ProtectedRoute>} />
        <Route path="/dashboard/backoffice/masters/area" element={<ProtectedRoute><AreaMaster /></ProtectedRoute>} />
        <Route path="/dashboard/backoffice/masters/zone" element={<ProtectedRoute><ZoneMaster /></ProtectedRoute>} />
        <Route path="/dashboard/backoffice/masters/pincode" element={<ProtectedRoute><PinCode /></ProtectedRoute>} />

        {/* --- Employee Management --- */}
        <Route path="/dashboard/backoffice/masters/designation" element={<ProtectedRoute><DesignationPage /></ProtectedRoute>} />
        <Route path="/dashboard/backoffice/masters/gst-master" element={<ProtectedRoute><GstMaster /></ProtectedRoute>} />
        <Route path="/dashboard/backoffice/masters/employee-type" element={<ProtectedRoute><EmployeeType /></ProtectedRoute>} />
        <Route path="/dashboard/backoffice/masters/employee" element={<ProtectedRoute><Employee /></ProtectedRoute>} />


        {/* --- Financial Masters --- */}
        <Route path="/dashboard/backoffice/masters/ledger" element={<ProtectedRoute><Ledger /></ProtectedRoute>} />
        <Route path="/dashboard/backoffice/masters/bank-ledger" element={<ProtectedRoute><BankLedger /></ProtectedRoute>} />
        <Route path="/dashboard/backoffice/masters/tax-rate-name" element={<ProtectedRoute><TaxRateNameMaster /></ProtectedRoute>} />
        <Route path="/dashboard/backoffice/masters/tax-slab" element={<ProtectedRoute><TaxSlabMaster /></ProtectedRoute>} />
        <Route path="/dashboard/backoffice/masters/tax-process" element={<ProtectedRoute><TaxProcessMaster /></ProtectedRoute>} />
        <Route path="/dashboard/backoffice/masters/language-master" element={<ProtectedRoute><LanguageMaster /></ProtectedRoute>} />
        <Route path="/dashboard/backoffice/role-permissions" element={<ProtectedRoute><RolePermissions /></ProtectedRoute>} />
        <Route path="/dashboard/backoffice/audit-log" element={<ProtectedRoute><AuditLogViewer /></ProtectedRoute>} />
        <Route path="/dashboard/backoffice/org/chain-brands" element={<ProtectedRoute><ChainBrandMaster /></ProtectedRoute>} />
        <Route path="/dashboard/backoffice/org/legal-entities" element={<ProtectedRoute><LegalEntityMaster /></ProtectedRoute>} />

        {/* --- Operational Masters --- */}
        <Route path="/dashboard/backoffice/masters/kds" element={<ProtectedRoute><KDSsettings /></ProtectedRoute>} />
        <Route path="/dashboard/backoffice/masters/visit-purpose" element={<ProtectedRoute><VisitPurpose /></ProtectedRoute>} />
        <Route path="/dashboard/backoffice/masters/pick-drop" element={<ProtectedRoute><PickDropFacility /></ProtectedRoute>} />
        <Route path="/dashboard/backoffice/masters/billing-instruction" element={<ProtectedRoute><BillingInstruction /></ProtectedRoute>} />
        <Route path="/dashboard/backoffice/masters/identity" element={<ProtectedRoute><IdentityDetails /></ProtectedRoute>} />
        <Route path="/dashboard/backoffice/masters/order-cancel-time" element={<ProtectedRoute><OrderCancelTimeMaster /></ProtectedRoute>} />
        <Route path="/dashboard/backoffice/masters/bill-no-setting" element={<ProtectedRoute><BillNoSettingMaster /></ProtectedRoute>} />
        <Route path="/dashboard/backoffice/masters/reservation-settings" element={<ProtectedRoute><ReservationSettings /></ProtectedRoute>} />
        <Route path="/dashboard/backoffice/masters/pickup-time" element={<ProtectedRoute><PickUpTime /></ProtectedRoute>} />
        <Route path="/dashboard/backoffice/masters/image-for-app" element={<ProtectedRoute><ImageDeliveryApp /></ProtectedRoute>} />
        <Route path="/dashboard/backoffice/masters/version" element={<ProtectedRoute><VersionPage /></ProtectedRoute>} />

        {/* --- Membership Masters --- */}
        <Route path="/dashboard/backoffice/masters/member-card" element={<ProtectedRoute><MemberCard /></ProtectedRoute>} />
        <Route path="/dashboard/backoffice/masters/topup-bonus" element={<ProtectedRoute><TopupBonus /></ProtectedRoute>} />
        <Route path="/dashboard/backoffice/membership/MemberShipType" element={<ProtectedRoute><MemberShipType /></ProtectedRoute>} />
        <Route path="/dashboard/backoffice/membership/MemberShipService" element={<ProtectedRoute><MemberShipService /></ProtectedRoute>} />
        <Route path="/dashboard/backoffice/membership/MemberShipPlan" element={<ProtectedRoute><MemberShipPlan /></ProtectedRoute>} />
        <Route path="/dashboard/backoffice/membership/plan-details" element={<ProtectedRoute><MemberShipPlanDetails /></ProtectedRoute>} />

        {/* --- Settings --- */}
        <Route path="/dashboard/backoffice/setting/Night-audit" element={<ProtectedRoute><NightAuditSetting /></ProtectedRoute>} />
        <Route path="/dashboard/backoffice/setting/Commonsetting" element={<ProtectedRoute><CommonSetting /></ProtectedRoute>} />
        <Route path="/dashboard/backoffice/setting/booking-no-setting" element={<ProtectedRoute><Bookingnositting /></ProtectedRoute>} />
        <Route path="/dashboard/backoffice/setting/copy-table-column-crm" element={<ProtectedRoute><CopyTableColumnCRM /></ProtectedRoute>} />

        {/* --- Common Masters --- */}
        <Route path="/dashboard/backoffice/CommonMaster/unit" element={<ProtectedRoute><UnitPage /></ProtectedRoute>} />
        <Route path="/dashboard/backoffice/CommonMaster/sub-unit" element={<ProtectedRoute><SubUnitPage /></ProtectedRoute>} />
        <Route path="/dashboard/backoffice/CommonMaster/fnb-billing-type" element={<ProtectedRoute><FnbBillingTypePage /></ProtectedRoute>} />
        <Route path="/dashboard/backoffice/CommonMaster/currency" element={<ProtectedRoute><CurrencyPage /></ProtectedRoute>} />
        <Route path="/dashboard/backoffice/CommonMaster/guest-type" element={<ProtectedRoute><GuestTypePage /></ProtectedRoute>} />
        <Route path="/dashboard/backoffice/CommonMaster/brand" element={<ProtectedRoute><BrandPage /></ProtectedRoute>} />
        <Route path="/dashboard/backoffice/CommonMaster/deals-booking-engine" element={<ProtectedRoute><DealsPage /></ProtectedRoute>} />
        <Route path="/dashboard/backoffice/CommonMaster/void-reasons" element={<ProtectedRoute><VoidReasonsPage /></ProtectedRoute>} />
        <Route path="/dashboard/backoffice/CommonMaster/kot-book-manual" element={<ProtectedRoute><KotBookManualPage /></ProtectedRoute>} />
        <Route path="/dashboard/backoffice/CommonMaster/printer-set" element={<ProtectedRoute><PrinterSetPage /></ProtectedRoute>} />
        <Route path="/dashboard/backoffice/CommonMaster/veg-type" element={<ProtectedRoute><VegTypePage /></ProtectedRoute>} />
        <Route path="/dashboard/backoffice/CommonMaster/business-source" element={<ProtectedRoute><BusinessSourcePage /></ProtectedRoute>} />

        {/* Communication Settings */}
        <Route path="/dashboard/backoffice/CommonMaster/emailer-creation" element={<ProtectedRoute><EmailerCreationPage /></ProtectedRoute>} />
        <Route path="/dashboard/backoffice/CommonMaster/email-setting" element={<ProtectedRoute><EmailSettingPage /></ProtectedRoute>} />
        <Route path="/dashboard/backoffice/CommonMaster/email-footer" element={<ProtectedRoute><EmailFooterPage /></ProtectedRoute>} />
        <Route path="/dashboard/backoffice/CommonMaster/whatsapp-setting" element={<ProtectedRoute><WhatsAppSettingPage /></ProtectedRoute>} />
        <Route path="/dashboard/backoffice/CommonMaster/sms-template" element={<ProtectedRoute><SmsTemplatePage /></ProtectedRoute>} />
        <Route path="/dashboard/backoffice/CommonMaster/add-client-whatsapp" element={<ProtectedRoute><AddClientWhatsAppPage /></ProtectedRoute>} />
        <Route path="/dashboard/backoffice/CommonMaster/submit-template" element={<ProtectedRoute><SubmitTemplatePage /></ProtectedRoute>} />
        <Route path="/dashboard/backoffice/CommonMaster/add-whatsapp-template" element={<ProtectedRoute><AddWhatsAppTemplatePage /></ProtectedRoute>} />

        {/* Other Settings */}
        <Route path="/dashboard/backoffice/CommonMaster/reg-doc-header-change" element={<ProtectedRoute><RegDocHeaderChangePage /></ProtectedRoute>} />
        <Route path="/dashboard/backoffice/CommonMaster/printer-setting" element={<ProtectedRoute><PrinterSettingPage /></ProtectedRoute>} />
        <Route path="/dashboard/backoffice/config/print-template" element={<ProtectedRoute><InvoiceConfiguration /></ProtectedRoute>} />
        <Route path="/dashboard/backoffice/CommonMaster/ledger-mapping" element={<ProtectedRoute><LedgerMappingPage /></ProtectedRoute>} />
        <Route path="/dashboard/backoffice/CommonMaster/tax-ledger-mapping" element={<ProtectedRoute><TaxLedgerMappingPage /></ProtectedRoute>} />
        <Route path="/dashboard/backoffice/CommonMaster/mapping-health" element={<ProtectedRoute><MappingCheckPage /></ProtectedRoute>} />
        <Route path="/dashboard/backoffice/CommonMaster/delete-night-audit-date" element={<ProtectedRoute><DeleteNightAuditDatePage /></ProtectedRoute>} />

        {/* --- F&B Backoffice --- */}
        <Route path="/dashboard/backoffice/fnb/AddPosCustomerSetting" element={<ProtectedRoute><AddPosCustomerSetting /></ProtectedRoute>} />
        <Route path="/dashboard/backoffice/fnb/CouponMaster" element={<ProtectedRoute><CouponMaster /></ProtectedRoute>} />
        <Route path="/dashboard/backoffice/fnb/Outlet" element={<ProtectedRoute><Outlet /></ProtectedRoute>} />
        <Route path="/dashboard/backoffice/fnb/Table" element={<ProtectedRoute><TablePage /></ProtectedRoute>} />
        <Route path="/dashboard/backoffice/fnb/ParentCategoryPage" element={<ProtectedRoute><ParentCategoryPage /></ProtectedRoute>} />
        <Route path="/dashboard/backoffice/fnb/Category" element={<ProtectedRoute><CategoryPage /></ProtectedRoute>} />
        <Route path="/dashboard/backoffice/fnb/MenuGroup" element={<ProtectedRoute><MenuGroupPage /></ProtectedRoute>} />
        <Route path="/dashboard/backoffice/fnb/Item" element={<ProtectedRoute><ItemPage /></ProtectedRoute>} />
        <Route path="/dashboard/backoffice/fnb/AddItemToOutlet" element={<ProtectedRoute><AddItemToOutletPage /></ProtectedRoute>} />
        <Route path="/dashboard/backoffice/fnb/OutOfStock" element={<ProtectedRoute><OutOfStock /></ProtectedRoute>} />
        <Route path="/dashboard/backoffice/fnb/AddKitchen" element={<ProtectedRoute><AddKitchen /></ProtectedRoute>} />
        <Route path="/dashboard/backoffice/fnb/KitchenTransferOutlet" element={<ProtectedRoute><KitchenTransferOutlet /></ProtectedRoute>} />
        <Route path="/dashboard/backoffice/fnb/BanquetGroup" element={<ProtectedRoute><BanquetGroup /></ProtectedRoute>} />
        <Route path="/dashboard/backoffice/fnb/LoyaltySetting" element={<ProtectedRoute><LoyaltySetting /></ProtectedRoute>} />
        <Route path="/dashboard/backoffice/fnb/Recipe" element={<ProtectedRoute><RecipePage /></ProtectedRoute>} />


        {/* --- Room Management --- */}
        <Route path="/dashboard/backoffice/room/ReservationCancelSetting" element={<ProtectedRoute><ReservationCancelSetting /></ProtectedRoute>} />
        <Route path="/dashboard/backoffice/room/BlockMaster" element={<ProtectedRoute><BlockMaster /></ProtectedRoute>} />
        <Route path="/dashboard/backoffice/room/FloorMaster" element={<ProtectedRoute><FloorMaster /></ProtectedRoute>} />
        <Route path="/dashboard/backoffice/room/RoomViewType" element={<ProtectedRoute><RoomViewType /></ProtectedRoute>} />
        <Route path="/dashboard/backoffice/room/BedSize" element={<ProtectedRoute><BedSize /></ProtectedRoute>} />
        <Route path="/dashboard/backoffice/room/AmenityCategoryMaster" element={<ProtectedRoute><AmenityCategoryMaster /></ProtectedRoute>} />
        <Route path="/dashboard/backoffice/room/AmenitySubCategoryMaster" element={<ProtectedRoute><AmenitySubCategoryMaster /></ProtectedRoute>} />
        <Route path="/dashboard/backoffice/room/RoomCategory" element={<ProtectedRoute><RoomCategory /></ProtectedRoute>} />
        <Route path="/dashboard/backoffice/room/RoomPlanType" element={<ProtectedRoute><RoomPlanType /></ProtectedRoute>} />
        <Route path="/dashboard/backoffice/room/RoomType" element={<ProtectedRoute><RoomType /></ProtectedRoute>} />
        <Route path="/dashboard/backoffice/room/RoomNoMaster" element={<ProtectedRoute><RoomNoMaster /></ProtectedRoute>} />
        <Route path="/dashboard/backoffice/room/RackRatePlanMaster" element={<ProtectedRoute><RackRatePlanMaster /></ProtectedRoute>} />
        <Route path="/dashboard/backoffice/room/RoomServiceTypeMaster" element={<ProtectedRoute><RoomServiceTypeMaster /></ProtectedRoute>} />
        <Route path="/dashboard/backoffice/room/RoomRatePlan" element={<ProtectedRoute><RoomRatePlan /></ProtectedRoute>} />
        <Route path="/dashboard/backoffice/room/RoomRackRatePlan" element={<ProtectedRoute><RoomRackRatePlan /></ProtectedRoute>} />
        <Route path="/dashboard/backoffice/room/RoomOffer" element={<ProtectedRoute><RoomOffer /></ProtectedRoute>} />
        <Route path="/dashboard/backoffice/room/ChannelManagerSetting" element={<ProtectedRoute><ChannelManagerSetting /></ProtectedRoute>} />
        <Route path="/dashboard/backoffice/room/CategoryDesignationMap" element={<ProtectedRoute><CategoryDesignationMap /></ProtectedRoute>} />
        <Route path="/dashboard/backoffice/room/ChannelManagerCodeSet" element={<ProtectedRoute><ChannelManagerCodeSet /></ProtectedRoute>} />
        <Route path="/dashboard/backoffice/room/MealPlanConfig" element={<ProtectedRoute><MealPlanConfig /></ProtectedRoute>} />


        {/* --- Currency Management --- */}
        <Route path="/dashboard/backoffice/currency/Setup" element={<ProtectedRoute><CurrencySetup /></ProtectedRoute>} />
        <Route path="/dashboard/backoffice/currency/Exchange" element={<ProtectedRoute><CurrencyExchange /></ProtectedRoute>} />

        {/* --- NC (Night Club) Management --- */}
        <Route path="/dashboard/backoffice/nc/NcCosting" element={<ProtectedRoute><NcCosting /></ProtectedRoute>} />
        <Route path="/dashboard/backoffice/nc/Department" element={<ProtectedRoute><Department /></ProtectedRoute>} />

        {/* --- Complaints Management --- */}
        <Route path="/dashboard/backoffice/complain/ComplainType" element={<ProtectedRoute><ComplainType /></ProtectedRoute>} />
        <Route path="/dashboard/backoffice/complain/ComplainUserList" element={<ProtectedRoute><ComplainUserList /></ProtectedRoute>} />

        {/* --- Production Management --- */}
        <Route path="/dashboard/backoffice/production/ProductionFinish" element={<ProtectedRoute><ProductionFinish /></ProtectedRoute>} />
        <Route path="/dashboard/backoffice/production/AddProduction" element={<ProtectedRoute><AddProduction /></ProtectedRoute>} />
        <Route path="/dashboard/backoffice/production/ProductionProcess" element={<ProtectedRoute><ProductionProcess /></ProtectedRoute>} />

        {/* --- Accounts Export --- */}
        <Route path="/dashboard/backoffice/accounts/ledger-export" element={<ProtectedRoute><LedgerExportPage /></ProtectedRoute>} />
        <Route path="/dashboard/backoffice/accounts/VoucherExport" element={<ProtectedRoute><VoucherExportPage /></ProtectedRoute>} />

        {/* --- QR Menu --- */}
        <Route path="/dashboard/backoffice/QRMaster/QRMenuSetting" element={<ProtectedRoute><QRMenuSettingPage /></ProtectedRoute>} />

        {/* --- Backoffice Reports --- */}
        <Route path="/dashboard/backoffice/report/UserList" element={<ProtectedRoute><UserList /></ProtectedRoute>} />
        <Route path="/dashboard/backoffice/report/FeedbackList" element={<ProtectedRoute><FeedbackList /></ProtectedRoute>} />


        {/* --- User Management --- */}
        <Route path="/dashboard/backoffice/user-management/user-role" element={<ProtectedRoute><UserRole /></ProtectedRoute>} />
        <Route path="/dashboard/backoffice/masters/user" element={<ProtectedRoute><UserCreation /></ProtectedRoute>} />
        <Route path="/dashboard/backoffice/user-management/user-rights" element={<ProtectedRoute><UserRights /></ProtectedRoute>} />
        <Route path="/dashboard/backoffice/user-management/user-authorization" element={<ProtectedRoute><UserAuthorization /></ProtectedRoute>} />

        {/* ============================================ */}
        {/* KDS (KITCHEN DISPLAY SYSTEM) MODULE */}
        {/* ============================================ */}
        <Route
          path="/dashboard/kds"
          element={
            <ProtectedRoute>
              <KdsPage />
            </ProtectedRoute>
          }
        />

        {/* ============================================ */}
        {/* BOOKING ENGINE MODULE */}
        {/* ============================================ */}
        <Route
          path="/dashboard/booking-engine"
          element={
            <ProtectedRoute>
              <BookingEnginePage />
            </ProtectedRoute>
          }
        />
        {/* Booking Engine Flow */}
        <Route path="/booking-engine/guest-details" element={<ProtectedRoute><GuestDetailsPage /></ProtectedRoute>} />
        <Route path="/booking-engine/summary" element={<ProtectedRoute><BookingSummaryPage /></ProtectedRoute>} />
        <Route path="/booking-engine/payment" element={<ProtectedRoute><PaymentPage /></ProtectedRoute>} />
        <Route path="/booking-engine/confirmation" element={<ProtectedRoute><ConfirmationPage /></ProtectedRoute>} />

        {/* ============================================ */}
        {/* REPORTS MODULE */}
        {/* ============================================ */}
        <Route
          path="/dashboard/reports"
          element={
            <ProtectedRoute>
              <ReportPage />
            </ProtectedRoute>
          }
        />

        {/* --- Reservation Reports --- */}
        <Route path="/dashboard/reports/Arrival" element={<ProtectedRoute><Arrival /></ProtectedRoute>} />
        <Route path="/dashboard/reports/ReservationAdvancePending" element={<ProtectedRoute><ReservationAdvancePending /></ProtectedRoute>} />
        <Route path="/dashboard/reports/ComplimentaryStay" element={<ProtectedRoute><ComplimentaryStay /></ProtectedRoute>} />
        <Route path="/dashboard/reports/ContributionAnalysis" element={<ProtectedRoute><ContributionAnalysis /></ProtectedRoute>} />
        <Route path="/dashboard/reports/CancelReservation" element={<ProtectedRoute><CancelReservation /></ProtectedRoute>} />
        <Route path="/dashboard/reports/Depature" element={<ProtectedRoute><Depature /></ProtectedRoute>} />
        <Route path="/dashboard/reports/DepartureForTheDay" element={<ProtectedRoute><DepartureForTheDay /></ProtectedRoute>} />
        <Route path="/dashboard/reports/NoShowReservation" element={<ProtectedRoute><NoShowReservation /></ProtectedRoute>} />
        <Route path="/dashboard/reports/ReservationForcast" element={<ProtectedRoute><ReservationForcast /></ProtectedRoute>} />
        <Route path="/dashboard/reports/RoomForcasting" element={<ProtectedRoute><RoomForcasting /></ProtectedRoute>} />
        <Route path="/dashboard/reports/CompanyForecastReport" element={<ProtectedRoute><CompanyForecastReport /></ProtectedRoute>} />
        <Route path="/dashboard/reports/SaleRevenue" element={<ProtectedRoute><SaleRevenue /></ProtectedRoute>} />

        {/* --- Front Office Reports --- */}
        <Route path="/dashboard/reports/GuestDetailsReport" element={<ProtectedRoute><GuestDetailsReport /></ProtectedRoute>} />
        <Route path="/dashboard/reports/GuestChecking" element={<ProtectedRoute><GuestChecking /></ProtectedRoute>} />
        <Route path="/dashboard/reports/DateWiseCheckout" element={<ProtectedRoute><DataWiseCheckout /></ProtectedRoute>} />
        <Route path="/dashboard/reports/GuestCheckout" element={<ProtectedRoute><GuestCheckout /></ProtectedRoute>} />
        <Route path="/dashboard/reports/GuestHighBalance" element={<ProtectedRoute><GuestHighBalance /></ProtectedRoute>} />
        <Route path="/dashboard/reports/RoomStatus" element={<ProtectedRoute><RoomStatus /></ProtectedRoute>} />
        <Route path="/dashboard/reports/AdvancePayment" element={<ProtectedRoute><AdvancePayment /></ProtectedRoute>} />
        <Route path="/dashboard/reports/BillSummary" element={<ProtectedRoute><BillSummary /></ProtectedRoute>} />
        <Route path="/dashboard/reports/BusinessSourceReport" element={<ProtectedRoute><BusinessSourceReport /></ProtectedRoute>} />
        <Route path="/dashboard/reports/CompanyAnalysis" element={<ProtectedRoute><CompanyAnalysis /></ProtectedRoute>} />
        <Route path="/dashboard/reports/DaywiseTariffDiscount" element={<ProtectedRoute><DaywiseTariffDiscount /></ProtectedRoute>} />
        <Route path="/dashboard/reports/DetailPosition" element={<ProtectedRoute><DetailPosition /></ProtectedRoute>} />
        <Route path="/dashboard/reports/CustomerDetails" element={<ProtectedRoute><CustomerDetails /></ProtectedRoute>} />
        <Route path="/dashboard/reports/RoomOccupancy" element={<ProtectedRoute><RoomOccupancy /></ProtectedRoute>} />
        <Route path="/dashboard/reports/ExpectedArrivalReport" element={<ProtectedRoute><ExpectedArrivalReport /></ProtectedRoute>} />
        <Route path="/dashboard/reports/ExpectedArrivalView" element={<ProtectedRoute><ExpectedArrivalView /></ProtectedRoute>} />
        <Route path="/dashboard/reports/ExpectedDepartureReport" element={<ProtectedRoute><ExpectedDepartureReport /></ProtectedRoute>} />
        <Route path="/dashboard/reports/IncomeTaxReport" element={<ProtectedRoute><IncomeTaxReport /></ProtectedRoute>} />
        <Route path="/dashboard/reports/InHouseReport" element={<ProtectedRoute><InHouseReport /></ProtectedRoute>} />
        <Route path="/dashboard/reports/InHouseForeignerReport" element={<ProtectedRoute><InHouseForeignerReport /></ProtectedRoute>} />
        <Route path="/dashboard/reports/FoCashReport" element={<ProtectedRoute><FoCashReport /></ProtectedRoute>} />
        <Route path="/dashboard/reports/OtherChargesReport" element={<ProtectedRoute><OtherChargesReport /></ProtectedRoute>} />
        <Route path="/dashboard/reports/SegmentReport" element={<ProtectedRoute><SegmentReport /></ProtectedRoute>} />
        <Route path="/dashboard/reports/NationalityDetailsReport" element={<ProtectedRoute><NationalityDetailsReport /></ProtectedRoute>} />
        <Route path="/dashboard/reports/MarketingReport" element={<ProtectedRoute><MarketingReport /></ProtectedRoute>} />
        <Route path="/dashboard/reports/SalesSummaryReport" element={<ProtectedRoute><SalesSummaryReport /></ProtectedRoute>} />
        <Route path="/dashboard/reports/RoomChangesReport" element={<ProtectedRoute><RoomChangesReport /></ProtectedRoute>} />
        <Route path="/dashboard/reports/MealPlanReport" element={<ProtectedRoute><MealPlanReport /></ProtectedRoute>} />
        <Route path="/dashboard/reports/OccupyProvisional" element={<ProtectedRoute><OccupyProvisional /></ProtectedRoute>} />
        <Route path="/dashboard/reports/RoomOtherPostServices" element={<ProtectedRoute><RoomOtherPostServices /></ProtectedRoute>} />
        <Route path="/dashboard/reports/DailyFlashReport" element={<ProtectedRoute><DailyFlashReports /></ProtectedRoute>} />
        <Route path="/dashboard/reports/PaymentCashierReport" element={<ProtectedRoute><PaymentCashierReports /></ProtectedRoute>} />
        <Route path="/dashboard/reports/ExtraServiceReport" element={<ProtectedRoute><ExtraServiceReports /></ProtectedRoute>} />
        <Route path="/dashboard/reports/OutstandingReport" element={<ProtectedRoute><OutstandingReport /></ProtectedRoute>} />
        <Route path="/dashboard/reports/PoliceReport" element={<ProtectedRoute><PoliceReports /></ProtectedRoute>} />
        <Route path="/dashboard/reports/TourismReport" element={<ProtectedRoute><TourismReports /></ProtectedRoute>} />
        <Route path="/dashboard/reports/GuestRoomOccupancy" element={<ProtectedRoute><GuestRoomOccupancyProvisionalReports /></ProtectedRoute>} />

        {/* --- F&B Reports --- */}
        <Route path="/dashboard/reports/ItemSaleAnalysis" element={<ProtectedRoute><ItemSaleAnalysisReports /></ProtectedRoute>} />
        <Route path="/dashboard/reports/SalesRegister" element={<ProtectedRoute><SalesRegister /></ProtectedRoute>} />
        <Route path="/dashboard/reports/ItemWiseSales" element={<ProtectedRoute><ItemWiseSales /></ProtectedRoute>} />
        <Route path="/dashboard/reports/GroupWiseSaleSummary" element={<ProtectedRoute><GroupWiseSaleSummary /></ProtectedRoute>} />
        <Route path="/dashboard/reports/CashierSale" element={<ProtectedRoute><CashierSale /></ProtectedRoute>} />
        <Route path="/dashboard/reports/NCTransaction" element={<ProtectedRoute><NCTransaction /></ProtectedRoute>} />
        <Route path="/dashboard/reports/CancelledBillsReport" element={<ProtectedRoute><CancelledBillsReport /></ProtectedRoute>} />
        <Route path="/dashboard/reports/PointOfSales" element={<ProtectedRoute><PointOfSales /></ProtectedRoute>} />

        {/* --- Audit Reports --- */}
        <Route path="/dashboard/reports/StatisticReport" element={<ProtectedRoute><StatisticReport /></ProtectedRoute>} />
        <Route path="/dashboard/reports/FlashReport" element={<ProtectedRoute><FlashReports /></ProtectedRoute>} />
        <Route path="/dashboard/reports/PaymentCollectionRegister" element={<ProtectedRoute><PaymentCollectionRegister /></ProtectedRoute>} />
        <Route path="/dashboard/reports/NightAuditSummary" element={<ProtectedRoute><NightAuditSummaryReports /></ProtectedRoute>} />
        <Route path="/dashboard/reports/NightAuditReport" element={<ProtectedRoute><NightAuditReport /></ProtectedRoute>} />
        <Route path="/dashboard/reports/DailyBusiness" element={<ProtectedRoute><DailyBusiness /></ProtectedRoute>} />
        <Route path="/dashboard/reports/DaySummaryReport" element={<ProtectedRoute><DaySummaryReport /></ProtectedRoute>} />
        <Route path="/dashboard/reports/DaySummaryMonthlyReport" element={<ProtectedRoute><DaySummaryMonthlyReport /></ProtectedRoute>} />
        <Route path="/dashboard/reports/OccupancyForecast" element={<ProtectedRoute><OccupancyForecast /></ProtectedRoute>} />
        <Route path="/dashboard/reports/DailyCheckInCheckOutReport" element={<ProtectedRoute><DailyCheckinCheckOutReport /></ProtectedRoute>} />
        <Route path="/dashboard/reports/RateCheck" element={<ProtectedRoute><RateCheck /></ProtectedRoute>} />
        <Route path="/dashboard/reports/RoomRevenue" element={<ProtectedRoute><RoomRevenue /></ProtectedRoute>} />
        <Route path="/dashboard/reports/RoomTypewiseSales" element={<ProtectedRoute><RoomTypewiseSales /></ProtectedRoute>} />
        <Route path="/dashboard/reports/RemoveTariffReport" element={<ProtectedRoute><RemoveTariffReport /></ProtectedRoute>} />
        <Route path="/dashboard/reports/RoomCharges" element={<ProtectedRoute><RoomChargesReport /></ProtectedRoute>} />
        <Route path="/dashboard/reports/RoomChargesMonth" element={<ProtectedRoute><RoomChargesMonthReport /></ProtectedRoute>} />
        <Route path="/dashboard/reports/RoomHistoryReport" element={<ProtectedRoute><RoomHistoryReport /></ProtectedRoute>} />
        <Route path="/dashboard/reports/NightAuditCheckList" element={<ProtectedRoute><NightAuditCheckList /></ProtectedRoute>} />
        <Route path="/dashboard/reports/UnbilledTransactionReport" element={<ProtectedRoute><UnbilledTransactionReport /></ProtectedRoute>} />
        <Route path="/dashboard/reports/ExecutiveSummaryReport" element={<ProtectedRoute><ExecutiveSummaryReport /></ProtectedRoute>} />
        <Route path="/dashboard/reports/DaywiseSalesReport" element={<ProtectedRoute><DaywiseSalesReport /></ProtectedRoute>} />
        <Route path="/dashboard/reports/AccessHistoryReport" element={<ProtectedRoute><AccessHistoryReport /></ProtectedRoute>} />
        <Route path="/dashboard/reports/AuditTrailReport" element={<ProtectedRoute><AuditTrailReport /></ProtectedRoute>} />
        <Route path="/dashboard/reports/LogReports" element={<ProtectedRoute><LogReports /></ProtectedRoute>} />
        <Route path="/dashboard/reports/GuestLedgerReport" element={<ProtectedRoute><GuestLedgerReport /></ProtectedRoute>} />

        {/* --- Inventory Reports --- */}
        <Route path="/dashboard/reports/StockLedger" element={<ProtectedRoute><StockLedger /></ProtectedRoute>} />
        <Route path="/dashboard/reports/StockLedgerSummary" element={<ProtectedRoute><StockLedgerSummary /></ProtectedRoute>} />
        <Route path="/dashboard/reports/ClosingStock" element={<ProtectedRoute><ClosingStock /></ProtectedRoute>} />

        {/* --- Housekeeping Reports --- */}
        <Route path="/dashboard/reports/RoomBoyCheckinList" element={<ProtectedRoute><RoomBoyCheckinList /></ProtectedRoute>} />
        <Route path="/dashboard/reports/HouseKeepingStatusAssigen" element={<ProtectedRoute><HouseKeepingStatusAssigen /></ProtectedRoute>} />
        <Route path="/dashboard/reports/RoomClearanceStaffAllocation" element={<ProtectedRoute><RoomClearanceStaffAllocation /></ProtectedRoute>} />
        <Route path="/dashboard/reports/BlockRoomWiseHouseKeeping" element={<ProtectedRoute><BlockRoomWiseHouseKeeping /></ProtectedRoute>} />
        <Route path="/dashboard/reports/ClearRoomReport" element={<ProtectedRoute><ClearRoomReport /></ProtectedRoute>} />
        <Route path="/dashboard/reports/BlockRoomReport" element={<ProtectedRoute><BlockRoomReport /></ProtectedRoute>} />
        <Route path="/dashboard/reports/MaintenanceRequestReport" element={<ProtectedRoute><MaintenanceRequestReport /></ProtectedRoute>} />
        <Route path="/dashboard/reports/LaundrySalesReport" element={<ProtectedRoute><LaundrySalesReport /></ProtectedRoute>} />
        <Route path="/dashboard/reports/LaundryBillWiseReport" element={<ProtectedRoute><LaundryBillWiseReport /></ProtectedRoute>} />
        <Route path="/dashboard/reports/LaundryNCSalesReport" element={<ProtectedRoute><LaundryNCSalesReport /></ProtectedRoute>} />
        <Route path="/dashboard/reports/MinibarSalesReport" element={<ProtectedRoute><MinibarSalesReport /></ProtectedRoute>} />
        <Route path="/dashboard/reports/MinibarNCSalesReport" element={<ProtectedRoute><MinibarNCSalesReport /></ProtectedRoute>} />
        <Route path="/dashboard/reports/LaundryDuplicatePrint" element={<ProtectedRoute><LaundryDuplicatePrint /></ProtectedRoute>} />
        <Route path="/dashboard/reports/LaundryItemWiseSalesReport" element={<ProtectedRoute><LaundryItemWiseSalesReport /></ProtectedRoute>} />
        <Route path="/dashboard/reports/MinibarDuplicatePrint" element={<ProtectedRoute><MinibarDuplicatePrint /></ProtectedRoute>} />
        <Route path="/dashboard/reports/MinibarItemWiseSalesReport" element={<ProtectedRoute><MinibarItemWiseSalesReport /></ProtectedRoute>} />
        <Route path="/dashboard/reports/IssueReport" element={<ProtectedRoute><IssueReport /></ProtectedRoute>} />
        <Route path="/dashboard/reports/inventory/StockReport" element={<ProtectedRoute><StockReport /></ProtectedRoute>} />
        <Route path="/dashboard/reports/inventory/PurchaseReport" element={<ProtectedRoute><PurchaseReport /></ProtectedRoute>} />
        <Route path="/dashboard/reports/inventory/GRNReportSummary" element={<ProtectedRoute><GRNReportSummary /></ProtectedRoute>} />
        <Route path="/dashboard/reports/inventory/GRNReportDetailed" element={<ProtectedRoute><GRNReportDetailed /></ProtectedRoute>} />
        <Route path="/dashboard/reports/inventory/IndentReport" element={<ProtectedRoute><IndentReport /></ProtectedRoute>} />
        <Route path="/dashboard/reports/inventory/IssueConsolidateReport" element={<ProtectedRoute><IssueConsolidateReport /></ProtectedRoute>} />
        <Route path="/dashboard/reports/inventory/SupplierWiseItem" element={<ProtectedRoute><SupplierWiseItem /></ProtectedRoute>} />
        <Route path="/dashboard/reports/inventory/ItemMasterReport" element={<ProtectedRoute><ItemMasterReport /></ProtectedRoute>} />
        <Route path="/dashboard/reports/inventory/OutOfStockReport" element={<ProtectedRoute><OutOfStockReport /></ProtectedRoute>} />
        <Route path="/dashboard/reports/inventory/SupplierWiseReport" element={<ProtectedRoute><SupplierWiseReport /></ProtectedRoute>} />
        <Route path="/dashboard/reports/inventory/CostCentreConsum" element={<ProtectedRoute><CostCentreConsum /></ProtectedRoute>} />
        <Route path="/dashboard/reports/inventory/StorewiseIssue" element={<ProtectedRoute><StorewiseIssue /></ProtectedRoute>} />
        <Route path="/dashboard/reports/inventory/PurchaseFlash" element={<ProtectedRoute><PurchaseFlash /></ProtectedRoute>} />
        <Route path="/dashboard/reports/inventory/PhysicalStockReport" element={<ProtectedRoute><PhysicalStockReport /></ProtectedRoute>} />
        <Route path="/dashboard/reports/inventory/SupplierGRNReport" element={<ProtectedRoute><SupplierGRNReport /></ProtectedRoute>} />
        <Route path="/dashboard/reports/inventory/EPurchaseReport" element={<ProtectedRoute><EPurchaseReport /></ProtectedRoute>} />
        <Route path="/dashboard/reports/inventory/AdjustmentReport" element={<ProtectedRoute><AdjustmentReport /></ProtectedRoute>} />

        {/* --- Inventory Transactions --- */}
        <Route path="/dashboard/inventory/direct-issue" element={<ProtectedRoute><DirectIssue /></ProtectedRoute>} />
        <Route path="/dashboard/inventory/department-requisition" element={<ProtectedRoute><DepartmentRequisition /></ProtectedRoute>} />
        <Route path="/dashboard/inventory/dept-req-approval" element={<ProtectedRoute><DeptReqApproval /></ProtectedRoute>} />
        <Route path="/dashboard/inventory/purchase-order" element={<ProtectedRoute><PurchaseOrderEntry /></ProtectedRoute>} />
        <Route path="/dashboard/inventory/purchase-approval" element={<ProtectedRoute><PurchaseApproval /></ProtectedRoute>} />
        <Route path="/dashboard/inventory/grn-receipts" element={<ProtectedRoute><GRNReceipts /></ProtectedRoute>} />
        <Route path="/dashboard/inventory/direct-receipts" element={<ProtectedRoute><DirectReceipts /></ProtectedRoute>} />
        <Route path="/dashboard/inventory/indents" element={<ProtectedRoute><IndentEntry /></ProtectedRoute>} />
        <Route path="/dashboard/inventory/indents-approval" element={<ProtectedRoute><IndentsApproval /></ProtectedRoute>} />
        <Route path="/dashboard/inventory/issues" element={<ProtectedRoute><IssuesToDo /></ProtectedRoute>} />
        <Route path="/dashboard/inventory/issue-return" element={<ProtectedRoute><IssueReturn /></ProtectedRoute>} />
        <Route path="/dashboard/inventory/grn-return" element={<ProtectedRoute><GRNReturn /></ProtectedRoute>} />
        <Route path="/dashboard/inventory/adjustment-stock" element={<ProtectedRoute><AdjustmentStock /></ProtectedRoute>} />
        <Route path="/dashboard/inventory/close-indent-po" element={<ProtectedRoute><CloseIndentPO /></ProtectedRoute>} />
        <Route path="/dashboard/inventory/monthend-closing-stock" element={<ProtectedRoute><MonthendClosingStock /></ProtectedRoute>} />

        {/* --- Inventory Masters --- */}
        <Route path="/dashboard/inventory/master/store-master"          element={<ProtectedRoute><StoreMaster /></ProtectedRoute>} />
        <Route path="/dashboard/inventory/master/group-master"          element={<ProtectedRoute><GroupMaster /></ProtectedRoute>} />
        <Route path="/dashboard/inventory/master/uom-master"            element={<ProtectedRoute><UOMMaster /></ProtectedRoute>} />
        <Route path="/dashboard/inventory/master/cost-centre"           element={<ProtectedRoute><CostCentreMaster /></ProtectedRoute>} />
        <Route path="/dashboard/inventory/master/item-master"           element={<ProtectedRoute><ItemMasterInv /></ProtectedRoute>} />
        <Route path="/dashboard/inventory/master/tag-items-cost-centre" element={<ProtectedRoute><TagItemsCostCentre /></ProtectedRoute>} />
        <Route path="/dashboard/inventory/master/cost-centre-items"     element={<ProtectedRoute><CostCentreItems /></ProtectedRoute>} />
        <Route path="/dashboard/inventory/master/supplier-master"       element={<ProtectedRoute><SupplierMaster /></ProtectedRoute>} />
        <Route path="/dashboard/inventory/master/tax-code"              element={<ProtectedRoute><TaxCodeMaster /></ProtectedRoute>} />
        <Route path="/dashboard/inventory/master/tax-structure"         element={<ProtectedRoute><TaxStructureMaster /></ProtectedRoute>} />

        {/* --- Back Office Reports --- */}
        <Route path="/dashboard/reports/OutstandingPayable" element={<ProtectedRoute><OutstandingPayable /></ProtectedRoute>} />
        <Route path="/dashboard/reports/BillWiseOutstanding" element={<ProtectedRoute><BillWiseOutstanding /></ProtectedRoute>} />
        <Route path="/dashboard/reports/LedgerStatementReport" element={<ProtectedRoute><LedgerStatementReport /></ProtectedRoute>} />
        <Route path="/dashboard/reports/AgingDebtorsDetails" element={<ProtectedRoute><AgingDebtorsDetails /></ProtectedRoute>} />
        <Route path="/dashboard/reports/AgingDebtorsSummary" element={<ProtectedRoute><AgingDebtorsSummary /></ProtectedRoute>} />
        <Route path="/dashboard/reports/CityLedgerDetails" element={<ProtectedRoute><CityLedgerDetails /></ProtectedRoute>} />
        <Route path="/dashboard/reports/CityLedgerSummary" element={<ProtectedRoute><CityLedgerSummary /></ProtectedRoute>} />
        <Route path="/dashboard/reports/CompanyLedger" element={<ProtectedRoute><CompanyLedger /></ProtectedRoute>} />
        <Route path="/dashboard/reports/OutstandingReceivable" element={<ProtectedRoute><OutstandingReceivable /></ProtectedRoute>} />
        <Route path="/dashboard/reports/AdvanceDetails" element={<ProtectedRoute><AdvanceDetails /></ProtectedRoute>} />
        <Route path="/dashboard/reports/DayBookSummary" element={<ProtectedRoute><DayBookSummaryReports /></ProtectedRoute>} />
        <Route path="/dashboard/reports/BtoB" element={<ProtectedRoute><BtoB /></ProtectedRoute>} />

        {/* --- Petty Cash Reports --- */}
        <Route path="/dashboard/reports/Pettycash" element={<ProtectedRoute><Pettycash /></ProtectedRoute>} />

        {/* --- Guest Reports --- */}
        <Route path="/dashboard/reports/GuestList" element={<ProtectedRoute><GuestList /></ProtectedRoute>} />
        <Route path="/dashboard/reports/GuestHistoryReport" element={<ProtectedRoute><GuestHistoryReport /></ProtectedRoute>} />

        {/* --- Cash Book Detailed Report (Front Office) --- */}
        <Route path="/dashboard/reports/CashBookDetailReport" element={<ProtectedRoute><CashBookDetailReport /></ProtectedRoute>} />


        {/* --- Banquet Reports --- */}
        <Route path="/dashboard/reports/BanquetBookingBook" element={<ProtectedRoute><BanquetBookingBook /></ProtectedRoute>} />
        <Route path="/dashboard/reports/FpWiseBanquetBooking" element={<ProtectedRoute><FpWiseBanquetBooking /></ProtectedRoute>} />

        {/* ============================================ */}
        {/* CHANNEL MANAGER MODULE */}
        {/* ============================================ */}
        <Route path="/dashboard/channel-manager/bookings" element={<ProtectedRoute><OTABookings /></ProtectedRoute>} />
        <Route path="/dashboard/channel-manager/config" element={<ProtectedRoute><STAAHConfig /></ProtectedRoute>} />
        <Route path="/dashboard/channel-manager/mapping" element={<ProtectedRoute><ChannelManagerCodeSet /></ProtectedRoute>} />
        <Route path="/dashboard/channel-manager/performance" element={<ProtectedRoute><PerformanceReport /></ProtectedRoute>} />
        <Route path="/dashboard/channel-manager/sync-status" element={<ProtectedRoute><SyncStatus /></ProtectedRoute>} />

        {/* ============================================ */}
        {/* PAYROLL MODULE */}
        {/* ============================================ */}
        <Route path="/dashboard/payroll" element={<ProtectedRoute><PayrollPage /></ProtectedRoute>} />
        <Route path="/payroll/employees" element={<ProtectedRoute><EmployeePayroll /></ProtectedRoute>} />
        <Route path="/payroll/salary-structure" element={<ProtectedRoute><SalaryStructurePage /></ProtectedRoute>} />
        <Route path="/payroll/attendance" element={<ProtectedRoute><AttendanceSummaryPage /></ProtectedRoute>} />
        <Route path="/payroll/process" element={<ProtectedRoute><ProcessPayroll /></ProtectedRoute>} />
        <Route path="/payroll/payslips" element={<ProtectedRoute><PayslipsPage /></ProtectedRoute>} />
        <Route path="/payroll/leave" element={<ProtectedRoute><LeaveManagementPage /></ProtectedRoute>} />
        <Route path="/payroll/overtime" element={<ProtectedRoute><OvertimeApprovalPage /></ProtectedRoute>} />
        <Route path="/payroll/roster" element={<ProtectedRoute><RosterSchedulingPage /></ProtectedRoute>} />
        <Route path="/payroll/full-final" element={<ProtectedRoute><FullFinalSettlementPage /></ProtectedRoute>} />
        <Route path="/ess/profile" element={<ProtectedRoute><MyProfilePage /></ProtectedRoute>} />
        <Route path="/ess/payslips" element={<ProtectedRoute><MyPayslipsPage /></ProtectedRoute>} />
        <Route path="/ess/leave-overtime" element={<ProtectedRoute><MyLeaveOvertimePage /></ProtectedRoute>} />
        <Route path="/ess/roster" element={<ProtectedRoute><MyRosterPage /></ProtectedRoute>} />
        <Route path="/mss/team" element={<ProtectedRoute><MyTeamPage /></ProtectedRoute>} />

        {/* ============================================ */}
        {/* FALLBACK ROUTE */}
        {/* ============================================ */}
        <Route path="*" element={<Navigate to="/dashboard" replace />} />
      </Routes>
      </Suspense>
      </WebSocketProvider>
    </BrowserRouter>
  );
}
